/**
 * TransferDetailPage - Warranty Champion's view of a single transfer.
 *
 * Live actions (approve / deny / cancel / hold / release / waive / upload
 * proof / request docs / request payment / record payment / verify proof)
 * are wired to the Phase 3+ API endpoints. Approve + Deny are elevated as
 * top-right primary buttons; every other action lives in a top-right
 * overflow menu (``CardActionMenu``) built from shared primitives, since
 * transfers are not driven by the workflow engine (no
 * ``WorkflowActionMenu``). Each entry is only shown for handlers the
 * Champion's role permits, so role-less users see fewer items and
 * permission-denied 403s surface via the toast helper.
 *
 * Card chrome follows the platform's standard record-detail pattern
 * (``RecordSignalStrip`` + ``DetailCard`` / ``EditableDetailCard``), the
 * same components Claims and Registrations use, so this page reads as one
 * consistent record surface rather than a bespoke layout. All detail cards
 * live in a single shared draggable masonry grid (``DraggableCardGrid``),
 * matching the Claims record.
 */

import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { AppShell } from '@/admin/components/app-shell'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Textarea } from '@/shared/components/ui/textarea'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { StatusBadge } from '@/admin/components/shared/status-badge'
import { DetailCard } from '@/admin/components/shared/detail-card'
import { NotesPanel } from '@/admin/components/shared/notes-panel'
import { RecordCommunicationsCard } from '@/admin/components/composer/record-communications-card'
import { ResourceContactsCard } from '@/admin/components/shared/resource-contacts-card'
import { EditableDetailCard } from '@/admin/components/shared/editable-detail-card'
import type { CardFieldItem } from '@/admin/components/shared/editable-card-types'
import {
  RecordSignalStrip,
  RecordSignal,
  SignalBadge,
} from '@/admin/components/shared/record-signal-strip'
import { DraggableCardGrid } from '@/admin/components/shared/draggable-card-grid'
import { PinMyViewBar } from '@/admin/components/shared/pin-my-view'
import { usePinnedView } from '@/admin/hooks/use-pinned-view'
import {
  CardActionMenu,
  type CardActionMenuItem,
} from '@/admin/components/shared/card-action-menu'
import { WorkflowActionMenu } from '@/shared/components/workflows'
import { AutoReminderAction } from '@/shared/components/reminders'
import { FeatureGate } from '@/admin/components/feature-gate'
import { FLAGS } from '@/admin/hooks/use-feature-flag'
import {
  useExecuteWorkflowAction,
  invalidateWorkflowInstanceQueries,
} from '@/shared/hooks/use-workflows'
import type { WorkflowAction } from '@/shared/lib/workflows/types'
import type { ActionLogEntry } from '@/admin/hooks/use-claims'
import { WorkflowTimelineEntry } from '@/admin/components/shared/workflow-timeline-entry'
import { ActionEntryDialog } from '@/shared/components/action-entry-dialog'
import { SENDER_ROLE_LABELS } from '@/admin/pages/claims/claim-detail-helpers'
import { toastApiError } from '@/shared/lib/api-error'
import { defineField } from '@/shared/lib/forms'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { useHoldings } from '@/admin/hooks/use-products'
import { useOrg } from '@/admin/hooks/use-org'
import {
  useOrgMembers,
  useClaimContactRoles,
  useCreateClaimContactRole,
} from '@/admin/hooks/use-claims'
import type { WarrantyTermsSynthesis } from '@/admin/types/products'
import {
  useTransferDetail,
  useTransfers,
  useUpdateTransfer,
  useRecordPaymentReceived,
  useWaiveFee,
  useTransferContacts,
  useAddTransferContact,
  useUpdateTransferContact,
  useRemoveTransferContact,
  useTransferDecisionReasons,
  useTransferDocumentChecklist,
  type EligibilityViolation,
  type Transfer,
} from '@/admin/hooks/use-transfers'
import { AttachmentsCard } from '@/admin/components/shared/attachments-card'
import { ReasonPickerDialog } from './components/reason-picker-dialog'
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BadgeDollarSign,
  BookOpen,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ClipboardCheck,
  Clock,
  CreditCard,
  Info,
  FileText,
  Landmark,
  Minus,
  Calendar,
  RotateCcw,
  User,
  UserCheck,
  Users,
  ShieldAlert,
  ShieldCheck,
  X,
} from 'lucide-react'
import { formatClaimId, formatDurationFromMonths, formatHoldingId } from '@/admin/lib/formatting'
import { useFormatters } from '@/shared/lib/formatting'

function daysBetween(iso: string | null, to?: string | null): number | null {
  if (!iso) return null
  const d = new Date(iso)
  const end = to ? new Date(to) : new Date()
  const ms = end.getTime() - d.getTime()
  return Math.floor(ms / (1000 * 60 * 60 * 24))
}

// ---------------------------------------------------------------------------
// Review-age signal - Champion SLA indicator, folded into the status strip.
//
// Same threshold logic the previous bespoke "days awaiting" chip used:
// 0 days -> neutral (fresh), 1 day -> success (within SLA), 2-4 -> warning
// (attention), 5+ -> warning (escalate; SignalBadge has no distinct danger
// tone). On terminal statuses (approved/denied/cancelled) it reports the
// decision latency instead ("Decided in N days") using reviewed_at.
// ---------------------------------------------------------------------------

type ReviewAgeTone = 'success' | 'warning' | 'neutral'

function getReviewAgeSignal(transfer: Transfer): {
  label: string
  tone: ReviewAgeTone
} {
  const msPerDay = 1000 * 60 * 60 * 24
  const isTerminal = ['approved', 'denied', 'cancelled'].includes(
    transfer.status,
  )

  if (isTerminal) {
    const decided = transfer.reviewed_at
      ? Math.max(
          0,
          Math.floor(
            (new Date(transfer.reviewed_at).getTime() -
              new Date(transfer.created_at).getTime()) /
              msPerDay,
          ),
        )
      : null
    if (decided === null) return { label: '-', tone: 'neutral' }
    return {
      label: `Decided in ${decided} ${decided === 1 ? 'day' : 'days'}`,
      tone: 'neutral',
    }
  }

  const daysOpen = Math.max(
    0,
    Math.floor((Date.now() - new Date(transfer.created_at).getTime()) / msPerDay),
  )
  const tone: ReviewAgeTone =
    daysOpen === 0 ? 'neutral' : daysOpen <= 1 ? 'success' : 'warning'
  const label =
    daysOpen === 0
      ? 'Review open today'
      : `Review open ${daysOpen} ${daysOpen === 1 ? 'day' : 'days'}`

  return { label, tone }
}

// ---------------------------------------------------------------------------
// Dialog-key union so we can route a single open handler to the right dialog.
// ---------------------------------------------------------------------------

type DialogKey =
  | 'approve'
  | 'deny'
  | 'cancel'
  | 'waive-fee'
  | 'request-docs'
  | 'request-payment'
  | null

export function TransferDetailPage() {
  const { uuid } = useParams<{ uuid: string }>()
  const navigate = useNavigate()
  const { data: transfer, isLoading, error } = useTransferDetail(uuid ?? null)
  // Reason lists come from the tenant's own TransferReason rows. They were
  // a hardcoded frontend constant named after one tenant, so every other
  // tenant saw that tenant's vocabulary.
  const {
    approval: approvalReasons,
    denial: denialReasons,
    isPending: reasonsPending,
  } = useTransferDecisionReasons()
  const { t } = useTranslation()
  const { can } = usePermissions()
  const { isPartnerAccess } = useOrg()
  const { formatDate } = useFormatters()
  const cardsView = usePinnedView('transfer-detail')

  const [dialogKey, setDialogKey] = useState<DialogKey>(null)

  // Lifecycle decisions (approve / deny / request docs / request payment /
  // hold / release / cancel) are driven by the generic workflow engine: the
  // top-right WorkflowActionMenu reads the seeded, permission-gated actions
  // from apps.workflows and every execution POSTs to engine.transition via
  // executeAction. ``pendingActionKey`` remembers which seeded action a rich
  // capture dialog (approve/deny/cancel/request-*) is executing when the menu
  // hands off via onActionClick.
  // ``transfer`` is undefined while the detail query is in flight; these run
  // before the isLoading / not-found guards below (hooks must precede early
  // returns), so read the uuid defensively.
  const instanceUuid = transfer?.workflow_instance_uuid ?? null
  const executeAction = useExecuteWorkflowAction(instanceUuid ?? '')
  const [pendingActionKey, setPendingActionKey] = useState<string | null>(null)
  const qc = useQueryClient()

  // §3/§10/§11: the request dialogs default their recipient to the transfer's
  // main contact (the notification lead) rather than the new owner. Resolve it
  // once here so both dialogs share the same source of truth.
  const { data: transferContacts } = useTransferContacts(transfer?.uuid ?? null)
  const mainContact = transferContacts?.find(
    (c) => c.is_main_contact && c.external_email,
  )
  const mainContactEmail = mainContact?.external_email ?? ''
  const mainContactName = mainContact?.external_name ?? ''

  // Refresh the transfer record + its activity log after an engine
  // transition so the status strip, cards, and action menu re-render for the
  // new state (executeAction only invalidates the workflow-instance queries).
  const invalidateTransfer = () => {
    qc.invalidateQueries({ queryKey: ['transfer', transfer.uuid] })
    qc.invalidateQueries({ queryKey: ['transfers'] })
    qc.invalidateQueries({ queryKey: ['transfer-activity', transfer.uuid] })
    // A decision changes the registrations' owner and transfer allowance.
    qc.invalidateQueries({ queryKey: ['holdings'] })
  }

  const waiveFee = useWaiveFee()
  // Mark Payment Received is a non-lifecycle card action (it stamps a date
  // without a state transition), so it stays on its own mutation + the compact
  // utility overflow, not the engine menu. Proof upload + verification are now
  // the Documents card (upload) and the engine Approve/Reject Proof actions.
  const recordPaymentFromMenu = useRecordPaymentReceived()

  const anyPending =
    executeAction.isPending ||
    waiveFee.isPending ||
    recordPaymentFromMenu.isPending

  // Gate the fetch-driven optional cards so the shared card grid never
  // renders an empty tile for a card that would return null. These reuse the
  // exact same query params the cards themselves use, so React Query serves
  // them from cache (no extra request).
  const hostHoldingsParams = transfer?.host_uuid
    ? { host_uuid: transfer.host_uuid, page_size: '1' }
    : undefined
  const { data: hostHoldingsData } = useHoldings(hostHoldingsParams)
  const hasWarranty = Boolean(hostHoldingsData?.results?.[0]?.warranties?.[0])
  const { data: hostTransfersData } = useTransfers(
    transfer?.host_uuid ? { host: transfer.host_uuid } : undefined,
  )
  const hasOtherTransfers =
    (hostTransfersData?.results ?? []).filter((t) => t.uuid !== transfer?.uuid)
      .length > 0

  if (isLoading) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <TransferDetailSkeleton />
        </main>
      </AppShell>
    )
  }

  if (error || !transfer) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <Button
            variant="link"
            onClick={() => navigate('/transfers')}
            className="mb-4"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to transfers
          </Button>
          <p className="text-[13px] text-[var(--status-error)]">
            Transfer not found.
          </p>
        </main>
      </AppShell>
    )
  }

  // Counted to the first contact, as the transfer window is; today until then.
  const daysSinceClosing = daysBetween(
    transfer.ownership_change_date,
    transfer.first_contact_date,
  )
  const reviewAge = getReviewAgeSignal(transfer)
  const requiresFee = transfer.transfer_type?.requires_fee !== false
  const requiresDocumentation =
    transfer.transfer_type?.requires_documentation !== false

  // ---- Action handlers ---------------------------------------------------

  const runMutation = async <T,>(
    mutate: () => Promise<T>,
    successMessage: string,
    failMessage: string,
  ) => {
    try {
      await mutate()
      toast.success(successMessage)
    } catch (err: unknown) {
      toastApiError(err, failMessage)
    }
  }

  // Execute a seeded workflow action through the engine (the pendingActionKey
  // was captured when the menu handed off to a rich capture dialog), then
  // refresh the record so the new state renders.
  const runWorkflowAction = (
    captured_values: Record<string, unknown>,
    successMessage: string,
    failMessage: string,
  ) => {
    const actionKey = pendingActionKey
    setDialogKey(null)
    setPendingActionKey(null)
    if (!actionKey) return
    runMutation(
      () =>
        executeAction
          .mutateAsync({ action_key: actionKey, captured_values })
          .then((res) => {
            invalidateTransfer()
            return res
          }),
      successMessage,
      failMessage,
    )
  }

  const handleApprove = (result: { reasonCode?: string }) => {
    runWorkflowAction(
      { approval_reason: result.reasonCode ?? '' },
      'Transfer approved.',
      'Failed to approve transfer',
    )
  }

  const handleDeny = (result: { reasonCode?: string }) => {
    if (!result.reasonCode) return
    runWorkflowAction(
      { denial_reason: result.reasonCode },
      'Transfer denied.',
      'Failed to deny transfer',
    )
  }

  const handleCancel = () => {
    runWorkflowAction({}, 'Transfer cancelled.', 'Failed to cancel transfer')
  }

  const handleWaiveFee = (result: { note?: string }) => {
    if (!result.note) return
    setDialogKey(null)
    runMutation(
      () => waiveFee.mutateAsync({ uuid: transfer.uuid, reason: result.note! }),
      'Fee waived.',
      'Failed to waive fee',
    )
  }

  const handleConfirmRequestDocs = (result: {
    message: string
    cc_emails: string[]
    setup_reminder: boolean
    auto_cancel: boolean
    requested_document_keys: string[]
    recipient_email: string
  }) => {
    runWorkflowAction(
      {
        doc_request_message: result.message,
        doc_request_cc: result.cc_emails,
        setup_reminder: result.setup_reminder,
        reminder_auto_cancel: result.auto_cancel,
        requested_document_keys: result.requested_document_keys,
        doc_request_recipient: result.recipient_email,
      },
      'Documents requested.',
      'Failed to request documents',
    )
  }

  const handleConfirmRequestPayment = (result: {
    recipient_email?: string
    message?: string
  }) => {
    runWorkflowAction(
      {
        recipient_email: result.recipient_email ?? '',
        payment_request_message: result.message ?? '',
      },
      'Payment request sent.',
      'Failed to request payment',
    )
  }

  const handleRecordPaymentFromMenu = () =>
    runMutation(
      () => recordPaymentFromMenu.mutateAsync({ uuid: transfer.uuid }),
      'Payment marked as received.',
      'Failed to record payment',
    )

  // ---- Top-right actions ---------------------------------------------------
  //
  // The lifecycle decisions (approve / deny / request docs / request payment /
  // hold / release / cancel) are driven by the engine WorkflowActionMenu
  // (top-right, below) - it reads the seeded, permission-gated, state-scoped
  // actions from apps.workflows, so this page no longer infers availability
  // from status strings. What remains here is the compact "utility" overflow
  // for the non-lifecycle card actions (upload / verify proof, record payment,
  // waive fee) - flag/date writes that are NOT workflow transitions.

  const isTerminal = ['approved', 'denied', 'cancelled'].includes(
    transfer.status,
  )

  const utilityMenuItems: CardActionMenuItem[] = []

  if (can('transfers.request_payment') && requiresFee && !transfer.payment_received) {
    utilityMenuItems.push({
      key: 'record-payment',
      label: 'Mark Payment Received',
      icon: <CheckCircle2 />,
      onSelect: handleRecordPaymentFromMenu,
      disabled: anyPending,
    })
  }
  if (can('transfers.waive_fee') && requiresFee && !isTerminal) {
    utilityMenuItems.push({
      key: 'waive-fee',
      label: 'Waive Fee',
      icon: <BadgeDollarSign />,
      onSelect: () => setDialogKey('waive-fee'),
      disabled: anyPending || transfer.fee_waived,
    })
  }

  // Route a clicked engine action to its rich capture dialog. Returning true
  // suppresses the generic confirm dialog (WorkflowActionMenu's default).
  // Actions with no rich capture (hold / release) fall through to the generic
  // dialog, which executes them via engine.transition directly.
  const handleWorkflowActionClick = (action: WorkflowAction): boolean | void => {
    setPendingActionKey(action.key)
    // Proof approve/reject (document-review stage) have no rich capture - let
    // the generic confirm dialog execute them via the engine. Checked before
    // the `approve_`/`deny_`-prefixed transfer-decision dialogs below so
    // `approve_proof_*` never routes to the "Approve this transfer?" flow.
    if (
      action.key.startsWith('approve_proof') ||
      action.key.startsWith('reject_proof')
    ) {
      setPendingActionKey(null)
      return
    }
    if (action.key.startsWith('approve_')) {
      // Approval waits for the fee: say what is missing rather than fail later.
      if (transfer.payment_outstanding) {
        setPendingActionKey(null)
        toast.error(
          'Mark the payment received or waive the fee before approving this transfer.',
        )
        return true
      }
      setDialogKey('approve')
      return true
    }
    if (action.key.startsWith('deny_')) {
      setDialogKey('deny')
      return true
    }
    if (action.key.startsWith('cancel_')) {
      setDialogKey('cancel')
      return true
    }
    if (action.key.startsWith('request_documents_')) {
      setDialogKey('request-docs')
      return true
    }
    if (action.key.startsWith('request_payment_')) {
      setDialogKey('request-payment')
      return true
    }
    // hold / release_hold: let the generic dialog confirm + execute.
    setPendingActionKey(null)
  }

  return (
    <AppShell>
      <main className="page-container py-10">
        <Button
          variant="link"
          onClick={() => navigate('/transfers')}
          className="mb-4 h-auto p-0 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)] hover:text-[var(--ink)]"
        >
          <ArrowLeft className="h-3 w-3" /> Back to transfers
        </Button>

        {/* Header - two-column grid mirroring the claims record: the text
            heading (label + name + address) on the left, the engine-driven
            action surface right-aligned on the right. Collapses to a single
            stacked column on mobile. Uses .clara-title so it sits below the
            page-level title ladder without feeling shouty; status + the rest
            of the record's vitals live in the signal strip below. */}
        <div className="mb-6 grid grid-cols-1 items-start gap-3 md:grid-cols-[1fr_1fr]">
          <div className="flex min-w-0 flex-col">
            <p className="clara-section-label mb-1.5">Transfer</p>
            <h1 className="clara-title m-0">
              {transfer.new_owner_first_name} {transfer.new_owner_last_name}
            </h1>
            <p className="mt-1.5 text-[12px] font-light text-[var(--ink-3)]">
              {transfer.host_address?.display || 'Property address pending'}
            </p>
          </div>

          <div
            className="flex flex-wrap items-center gap-2 md:justify-end"
            data-testid="transfer-actions"
          >
            {/* Auto-reminder top action (enable → status → details/cancel).
                Generic, reused across records; gated by the auto_reminders
                flag. Sits above the workflow action buttons. */}
            {!isTerminal && (
              <FeatureGate flag={FLAGS.AUTO_REMINDERS}>
                <AutoReminderAction
                  resourceType="transfer"
                  resourceUuid={transfer.uuid}
                />
              </FeatureGate>
            )}
            {/* Engine-driven action surface: the seeded warranty_transfer
                workflow's visible, permission-gated actions for the current
                state. Rich-capture actions (approve/deny/cancel/request-*)
                hand off to their dialogs via onActionClick; hold/release use
                the generic confirm. Rendered only once the transfer is bound
                to a workflow instance (always, for a seeded tenant). */}
            {instanceUuid && (
              <WorkflowActionMenu
                instanceUuid={instanceUuid}
                workflowKey="warranty_transfer"
                onActionExecuted={invalidateTransfer}
                onActionClick={handleWorkflowActionClick}
                // The menu renders its buttons in its own flex row; make it fill
                // the right column and right-align so the buttons sit flush right
                // (the outer justify-end can't reach inside the menu's wrapper).
                className="w-full justify-end"
              />
            )}
            {/* Utility overflow: non-lifecycle card actions (upload/verify
                proof, record payment, waive fee). */}
            {utilityMenuItems.length > 0 && (
              <CardActionMenu
                items={utilityMenuItems}
                triggerLabel="More transfer actions"
                data-testid="transfer-actions-menu"
              />
            )}
          </div>
        </div>

        <RecordSignalStrip className="mb-8">
          <RecordSignal
            label="Status"
            value={<StatusBadge status={transfer.status_label ?? transfer.status} />}
            sub="Current status"
          />
          <RecordSignal
            label="Transfer Type"
            value={transfer.transfer_type?.label ?? '-'}
            sub="Reason"
          />
          <RecordSignal
            label="First Contact"
            value={
              transfer.first_contact_date
                ? formatDate(transfer.first_contact_date)
                : '-'
            }
            sub="Call center"
          />
          <RecordSignal
            label="Closing Date"
            value={
              transfer.ownership_change_date
                ? formatDate(transfer.ownership_change_date)
                : '-'
            }
            sub="Ownership change"
          />
          <RecordSignal
            label="Days Since Closing"
            value={
              daysSinceClosing === null
                ? '-'
                : daysSinceClosing < 0
                  ? `${Math.abs(daysSinceClosing)} (future)`
                  : daysSinceClosing
            }
            sub={transfer.first_contact_date ? 'To first contact' : 'Elapsed'}
          />
          <RecordSignal
            label="Review Age"
            value={<SignalBadge tone={reviewAge.tone}>{reviewAge.label}</SignalBadge>}
            sub={
              ['approved', 'denied', 'cancelled'].includes(transfer.status)
                ? 'Decision latency'
                : 'Champion SLA'
            }
          />
        </RecordSignalStrip>

        {/* EVERY detail card lives in one shared draggable masonry grid
            (matching the Claims record) so the Champion can reorder any
            card to fit how they work - including the Original / New Owner
            pair. Conditionally-rendered cards (Payment, Proof, Other
            Transfers) are simply omitted; every other card handles its own
            empty state internally. */}
        <PinMyViewBar view={cardsView} />
        <DraggableCardGrid
          storageKey="transfer-detail-cards-v1"
          locked={cardsView.pinned}
          className="columns-1 gap-4 md:columns-2"
          itemClassName="mb-4 break-inside-avoid"
        >
          <OriginalOwnerCard key="original-owner" transfer={transfer} />
          <NewOwnerCard
            key="new-owner"
            transfer={transfer}
            canEdit={can('transfers.edit')}
          />
          {transfer.property_records && (
            <PropertyRecordsCard
              key="property-records"
              records={transfer.property_records}
            />
          )}
          <TransferContactsCard
            key="contacts"
            transfer={transfer}
            canEdit={can('transfers.edit')}
          />
          <ChampionApprovalCard
            key="agent-intake"
            transfer={transfer}
            requiresDocumentation={requiresDocumentation}
          />
          {requiresFee && <PaymentCard key="payment" transfer={transfer} can={can} />}
          <AttachmentsCard
            key="documents"
            subjectType="transfer"
            subjectUuid={transfer.uuid}
            useCase="PROOF_OF_OWNERSHIP"
            title="Documents"
            icon={<FileText className="h-3.5 w-3.5" />}
            canAdd={can('transfers.upload_proof') && !isTerminal}
            canDelete={can('transfers.edit')}
            canRelabel={can('transfers.edit') || can('attachments.recategorise')}
            labelPlaceholder="e.g. Proof of ownership"
            onChanged={() => {
              // A document upload can advance the transfer into document
              // review; refresh the transfer + its workflow action menu so
              // the new stage's actions appear without a manual reload.
              invalidateTransfer()
              if (instanceUuid) {
                invalidateWorkflowInstanceQueries(qc, instanceUuid)
              }
            }}
          />
          <NotesPanel
            key="notes"
            recordId={transfer.uuid}
            recordType="transfer"
            canAddInternal
            canAddCustomerVisible={false}
            allowAttachments
          />
          {/* What the transfer has sent, and Create New to send a letter or
              email from it (WPS-231). The team's record: partners never
              send from one. */}
          {!isPartnerAccess && (
            <RecordCommunicationsCard
              key="communications"
              data-card-title={t('claimComms.title', 'Communications')}
              recordType="transfer"
              recordUuid={transfer.uuid}
              testIdPrefix="transfer-comms"
              emptyLabel={t(
                'transfers.comms.empty',
                'Nothing has been sent on this transfer yet.',
              )}
              letterHint={t(
                'transfers.comms.letterHint',
                'A formal letter, kept on the transfer as a PDF',
              )}
            />
          )}
          {hasWarranty && <WarrantyTermsCard key="warranty" transfer={transfer} />}
          {hasWarranty && (
            <CoverageTimelineCard key="coverage" transfer={transfer} />
          )}
          {transfer.eligibility_snapshot.length > 0 && (
            <EligibilitySnapshotPanel
              key="eligibility"
              snapshot={transfer.eligibility_snapshot}
            />
          )}
          <ChampionReviewChecklist key="checklist" transferUuid={transfer.uuid} />
          {(transfer.override_reason?.trim() ||
            transfer.approval_reason ||
            transfer.denial_reason) && (
            <DecisionsAuditCard key="decisions" transfer={transfer} />
          )}
          {Object.keys(transfer.channel_metadata ?? {}).length > 0 && (
            <ChannelMetadataCard
              key="channel-metadata"
              metadata={transfer.channel_metadata}
            />
          )}
          <ActivityCard key="activity" history={transfer.workflow_history ?? []} />
          {transfer.host_uuid && hasOtherTransfers && (
            <OtherTransfersOnHost
              key="other-transfers"
              hostUuid={transfer.host_uuid}
              excludeUuid={transfer.uuid}
            />
          )}
          <LinkedRegistrationsCard key="linked-registrations" transfer={transfer} />
          {can('claims.view.global') && (
            <LinkedClaimsCard key="linked-claims" transfer={transfer} />
          )}
        </DraggableCardGrid>
      </main>

      {/* Reason dialogs - one mount, routed by dialogKey */}
      <ReasonPickerDialog
        open={dialogKey === 'approve'}
        onOpenChange={(next) => {
          if (!next) setDialogKey(null)
        }}
        title="Approve this transfer?"
        description={t(
          'transfers.detail.approveDescription',
          'The new owner takes over the {{holdingNounPluralLower}} in this transfer. The reason is recorded and included in the decision email.',
        )}
        options={approvalReasons}
        optionsLoading={reasonsPending}
        reasonRequired={true}
        confirmLabel="Approve"
        pending={executeAction.isPending}
        onConfirm={handleApprove}
      />

      <ReasonPickerDialog
        open={dialogKey === 'deny'}
        onOpenChange={(next) => {
          if (!next) setDialogKey(null)
        }}
        title="Deny this transfer?"
        description="The denial reason is captured for audit."
        options={denialReasons}
        optionsLoading={reasonsPending}
        reasonRequired={true}
        confirmLabel="Deny"
        confirmVariant="destructive"
        pending={executeAction.isPending}
        onConfirm={handleDeny}
      />

      <ReasonPickerDialog
        open={dialogKey === 'cancel'}
        onOpenChange={(next) => {
          if (!next) setDialogKey(null)
        }}
        title="Cancel this transfer?"
        description={t(
          'transfers.detail.cancelDescription',
          'Closes this transfer without a decision. The {{holdingNounPluralLower}} stay as they are and no transfer is used up.',
        )}
        confirmLabel="Cancel Transfer"
        confirmVariant="destructive"
        pending={executeAction.isPending}
        onConfirm={handleCancel}
      />

      <ReasonPickerDialog
        open={dialogKey === 'waive-fee'}
        onOpenChange={(next) => {
          if (!next) setDialogKey(null)
        }}
        title="Waive the transfer fee?"
        description="Discretionary fee waiver. The reason is required for audit."
        requireNote={true}
        noteLabel="Reason for waiver"
        noteMinLength={20}
        confirmLabel="Waive Fee"
        pending={waiveFee.isPending}
        onConfirm={handleWaiveFee}
      />

      {/* Request Documents capture dialog - the single entry point for
          both the review-panel action and the Proof card's resend
          button, since the backend now requires a message. */}
      <RequestDocumentsDialog
        open={dialogKey === 'request-docs'}
        onOpenChange={(next) => {
          if (!next) setDialogKey(null)
        }}
        transfer={transfer}
        mainContactEmail={mainContactEmail}
        mainContactName={mainContactName}
        pending={executeAction.isPending}
        onConfirm={handleConfirmRequestDocs}
      />

      <RequestPaymentDialog
        open={dialogKey === 'request-payment'}
        onOpenChange={(next) => {
          if (!next) setDialogKey(null)
        }}
        transfer={transfer}
        mainContactEmail={mainContactEmail}
        pending={executeAction.isPending}
        onConfirm={handleConfirmRequestPayment}
      />
    </AppShell>
  )
}

// ---------------------------------------------------------------------------
// Ownership Change - Original Owner / New Owner cards.
//
// Shows the ownership change as two standard editable-detail cards: who
// owned the property, and who owns it now. The original owner is pulled
// from the host's first active holding (the warranty registration roll is
// owned per host); the new owner comes directly from the transfer record.
// First contact date is editable on the New Owner card (PATCH via
// useUpdateTransfer) since it's part of the call-center intake for the
// incoming owner.
// ---------------------------------------------------------------------------

function OriginalOwnerCard({ transfer }: { transfer: Transfer }) {
  // The seller, resolved by the server: the property's owner until the
  // transfer is approved, then the owner it was moved from.
  const original = transfer.original_owner ?? null

  const name = original
    ? `${original.first_name ?? ''} ${original.last_name ?? ''}`.trim() ||
      'Unknown original owner'
    : 'Unknown original owner'

  const items: CardFieldItem[] = [
    {
      field: defineField({ code_name: 'name', display_name: 'Name', field_type: 'TEXT' }),
      value: name,
      readOnly: true,
    },
    {
      field: defineField({ code_name: 'email', display_name: 'Email', field_type: 'EMAIL' }),
      value: original?.email,
      readOnly: true,
      link: Boolean(original?.email),
      onLinkClick: original?.email
        ? () => {
            window.location.href = `mailto:${original.email}`
          }
        : undefined,
    },
    {
      field: defineField({ code_name: 'phone', display_name: 'Phone', field_type: 'PHONE' }),
      value: original?.phone_number,
      readOnly: true,
    },
  ]

  return (
    <EditableDetailCard
      title="Original Owner"
      icon={<User className="h-4 w-4" />}
      items={items}
      collapsible={false}
      trailing={
        <span className="text-[10px] font-light uppercase tracking-[0.12em] text-[var(--ink-3)]">
          Owner of record
        </span>
      }
    />
  )
}

function NewOwnerCard({
  transfer,
  canEdit,
}: {
  transfer: Transfer
  canEdit: boolean
}) {
  const { formatDate } = useFormatters()
  const updateTransfer = useUpdateTransfer()

  const caption = transfer.ownership_change_date
    ? `Incoming on ${formatDate(transfer.ownership_change_date)}`
    : 'Incoming warranty holder'
  // The new owner can be corrected until the transfer closes.
  const closed = ['approved', 'denied', 'cancelled'].includes(transfer.status)

  const items: CardFieldItem[] = [
    {
      field: defineField({ code_name: 'first_name', display_name: 'First Name', field_type: 'TEXT', required: true }),
      value: transfer.new_owner_first_name,
      readOnly: closed,
    },
    {
      field: defineField({ code_name: 'last_name', display_name: 'Last Name', field_type: 'TEXT', required: true }),
      value: transfer.new_owner_last_name,
      readOnly: closed,
    },
    {
      field: defineField({ code_name: 'email', display_name: 'Email', field_type: 'EMAIL' }),
      value: transfer.new_owner_email,
      readOnly: closed,
      link: Boolean(transfer.new_owner_email),
      onLinkClick: transfer.new_owner_email
        ? () => {
            window.location.href = `mailto:${transfer.new_owner_email}`
          }
        : undefined,
    },
    {
      field: defineField({ code_name: 'phone', display_name: 'Phone', field_type: 'PHONE' }),
      value: transfer.new_owner_phone,
      readOnly: closed,
    },
    {
      field: defineField({ code_name: 'first_contact_date', display_name: 'First Contact Date', field_type: 'DATE' }),
      value: transfer.first_contact_date,
    },
  ]

  const handleSave = async (values: Record<string, unknown>) => {
    try {
      await updateTransfer.mutateAsync({
        uuid: transfer.uuid,
        first_contact_date: (values.first_contact_date as string) || null,
        ...(closed
          ? {}
          : {
              new_owner_first_name: String(values.first_name ?? ''),
              new_owner_last_name: String(values.last_name ?? ''),
              new_owner_email: String(values.email ?? ''),
              new_owner_phone: String(values.phone ?? ''),
            }),
      })
      toast.success(closed ? 'First contact date updated.' : 'New owner updated.')
    } catch (err) {
      toastApiError(err, 'Failed to update the new owner')
      throw err
    }
  }

  return (
    <EditableDetailCard
      title="New Owner"
      icon={<UserCheck className="h-4 w-4" />}
      items={items}
      collapsible={false}
      editButtonTestId="edit-new-owner"
      editConfig={{
        onSave: handleSave,
        isPending: updateTransfer.isPending,
        canEdit,
      }}
      trailing={
        <span className="max-w-[160px] truncate text-[10px] font-light uppercase tracking-[0.12em] text-[var(--green)]">
          {caption}
        </span>
      }
    />
  )
}

// ---------------------------------------------------------------------------
// Contacts - every party the transfer already carries (an internal team
// member, or an external party like a realtor or closing agent). Renders
// through the SAME shared, presentational `ResourceContactsCard` the Claims
// record's Contacts card uses
// (`@/admin/components/shared/resource-contacts-card`), so the look and
// interaction model are identical between the two records; this wrapper
// only owns the transfer-specific data hooks. The transfer contact write
// endpoint takes a free-text `role_label` (no role FK), so the Role field
// still picks from the tenant-scoped, reusable role vocabulary via the
// Claims Contacts roles endpoint (`useClaimContactRoles` /
// `useCreateClaimContactRole` - resource-agnostic despite living in
// use-claims.ts, per the shared component's `roles` / `onCreateRole`
// props), but this wrapper sends the resolved LABEL, not the role uuid.
// ---------------------------------------------------------------------------

function TransferContactsCard({
  transfer,
  canEdit,
}: {
  transfer: Transfer
  canEdit: boolean
}) {
  const { orgUuid } = useOrg()
  const { data: contacts, isPending } = useTransferContacts(transfer.uuid)
  const { data: roles = [] } = useClaimContactRoles()
  // The org-members list is only needed for the Add dialog's team-member
  // picker, so it is fetched lazily (only while that dialog is open) -
  // matching the pre-extraction behaviour of not hitting
  // `/organisations/{uuid}/members/` on every transfer view.
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const { data: orgMembers } = useOrgMembers(addDialogOpen ? orgUuid : null)
  const createRole = useCreateClaimContactRole()
  const addContact = useAddTransferContact()
  const updateContact = useUpdateTransferContact()
  const removeContact = useRemoveTransferContact()

  return (
    <ResourceContactsCard
      title="Contacts"
      icon={<Users className="h-4 w-4" />}
      collapsible={false}
      contacts={contacts ?? []}
      isLoading={isPending}
      canEdit={canEdit}
      orgMembers={orgMembers ?? []}
      roles={roles}
      onAddOpenChange={setAddDialogOpen}
      onAdd={(payload) =>
        new Promise<void>((resolve, reject) => {
          addContact.mutate(
            {
              uuid: transfer.uuid,
              user: payload.user,
              external_name: payload.external_name,
              external_email: payload.external_email,
              external_phone: payload.external_phone,
              role_label: payload.role_label,
              permission: payload.permission,
              is_main_contact: payload.is_main_contact,
            },
            {
              onSuccess: () => resolve(),
              onError: (err) => reject(err),
            },
          )
        })
      }
      onUpdate={(contactUuid, payload) =>
        new Promise<void>((resolve, reject) => {
          updateContact.mutate(
            {
              uuid: transfer.uuid,
              contactUuid,
              external_name: payload.external_name,
              external_email: payload.external_email,
              external_phone: payload.external_phone,
              role_label: payload.role_label,
              permission: payload.permission,
              is_main_contact: payload.is_main_contact,
            },
            {
              onSuccess: () => resolve(),
              onError: (err) => reject(err),
            },
          )
        })
      }
      onRemove={(contactUuid) =>
        new Promise<void>((resolve, reject) => {
          removeContact.mutate(
            { uuid: transfer.uuid, contactUuid },
            {
              onSuccess: () => resolve(),
              onError: (err) => reject(err),
            },
          )
        })
      }
      onCreateRole={(label) =>
        new Promise<{ value: string }>((resolve, reject) => {
          createRole.mutate(
            { label },
            {
              onSuccess: (role) => resolve({ value: role.uuid }),
              onError: (err) => reject(err),
            },
          )
        })
      }
    />
  )
}

// ---------------------------------------------------------------------------
// Payment card - fee, waiver, payment method/reference, and payment-received
// tracking. Hidden entirely by the parent when the transfer type doesn't
// require a fee (data-driven via transfer.transfer_type.requires_fee).
// ---------------------------------------------------------------------------

function PaymentCard({
  transfer,
  can,
}: {
  transfer: Transfer
  can: (codename: string) => boolean
}) {
  const { locale, currency } = useFormatters()
  const updateTransfer = useUpdateTransfer()
  const recordPayment = useRecordPaymentReceived()

  const items: CardFieldItem[] = [
    {
      field: defineField({ code_name: 'fee_amount', display_name: 'Fee Amount', field_type: 'MONEY' }),
      value: transfer.fee_amount,
      // Editable. `value` (the bare decimal string) drives the read-only
      // display; `editValue` seeds the CurrencyInput with the {amount, currency}
      // shape it expects, in the org's default currency. The fee is stored as a
      // plain DecimalField (no currency column), so the currency is the org
      // default and is dropped on save - handleSave sends only the amount.
      editValue: {
        amount:
          transfer.fee_amount != null && transfer.fee_amount !== ''
            ? Number(transfer.fee_amount)
            : undefined,
        currency,
      },
    },
    {
      field: defineField({ code_name: 'fee_waived', display_name: 'Fee Waived', field_type: 'BOOLEAN' }),
      value: transfer.fee_waived,
      displayValue: transfer.fee_waived ? (
        <span className="text-[13px] text-[var(--status-warn)]">Yes</span>
      ) : (
        <span className="text-[13px] text-[var(--ink)]">No</span>
      ),
      readOnly: true,
    },
    {
      field: defineField({ code_name: 'fee_waived_reason', display_name: 'Waiver Reason', field_type: 'TEXT' }),
      value: transfer.fee_waived_reason,
      readOnly: true,
      hideWhenEmptyDisplay: true,
    },
    {
      field: defineField({
        code_name: 'payment_method',
        display_name: 'Payment Method',
        field_type: 'SELECT',
        options: [
          { value: 'check', label: 'Check' },
          { value: 'online', label: 'Pay Online' },
        ],
      }),
      value: transfer.payment_method,
    },
    {
      field: defineField({ code_name: 'payment_reference', display_name: 'Payment Reference', field_type: 'TEXT' }),
      value: transfer.payment_reference,
    },
    {
      field: defineField({ code_name: 'payment_received', display_name: 'Payment Received', field_type: 'BOOLEAN' }),
      value: transfer.payment_received,
      displayValue: transfer.payment_received ? (
        <span className="inline-flex items-center gap-1 text-[13px] text-[var(--status-success)]">
          <CheckCircle2 className="h-3.5 w-3.5" /> Yes
        </span>
      ) : (
        <span className="text-[13px] text-[var(--ink-3)]">No</span>
      ),
      readOnly: true,
    },
    {
      // §15: the received date is editable/backdatable. Payments are sometimes
      // taken before they are logged, so the auto-stamped "today" must be
      // correctable. Saving a changed date routes through record_payment_received
      // (see handleSave), which is idempotent - it updates the stored date and,
      // only when the transfer is still awaiting payment, marks it received and
      // advances the state. A plain DATE (not DATE_TIME): the backend column is a
      // DateField, and a day is the meaningful precision for a backdated receipt.
      field: defineField({ code_name: 'payment_received_at', display_name: 'Received Date', field_type: 'DATE' }),
      value: transfer.payment_received_at,
      editValue: transfer.payment_received_at ?? undefined,
      hideWhenEmptyDisplay: true,
    },
  ]

  const handleSave = async (values: Record<string, unknown>) => {
    // The MONEY field yields { amount, currency }; the fee is a bare decimal,
    // so we persist only the amount (org-default currency is implicit). An
    // empty amount clears the fee (null).
    const fee = values.fee_amount as
      | { amount?: number | string | null; currency?: string }
      | null
      | undefined
    const feeAmount =
      fee && fee.amount != null && fee.amount !== ''
        ? String(fee.amount)
        : null
    // §15: a changed received date is persisted via record_payment_received
    // (the field is not part of the detail-edit whitelist). Only fires when the
    // value actually changed, so a normal fee/reference edit does not re-record.
    const receivedRaw = values.payment_received_at
    const receivedDate =
      typeof receivedRaw === 'string' && receivedRaw !== '' ? receivedRaw.slice(0, 10) : null
    const receivedChanged =
      receivedDate != null && receivedDate !== (transfer.payment_received_at?.slice(0, 10) ?? null)
    try {
      await updateTransfer.mutateAsync({
        uuid: transfer.uuid,
        fee_amount: feeAmount,
        payment_method: (values.payment_method as string) ?? '',
        payment_reference: (values.payment_reference as string) ?? '',
      })
      if (receivedChanged) {
        await recordPayment.mutateAsync({ uuid: transfer.uuid, received_date: receivedDate })
      }
      toast.success('Payment details updated.')
    } catch (err) {
      toastApiError(err, 'Failed to update payment details')
      throw err
    }
  }

  const handleRecordPayment = async () => {
    try {
      await recordPayment.mutateAsync({ uuid: transfer.uuid })
      toast.success('Payment marked as received.')
    } catch (err) {
      toastApiError(err, 'Failed to record payment')
    }
  }

  return (
    <EditableDetailCard
      title="Payment"
      icon={<CreditCard className="h-4 w-4" />}
      items={items}
      collapsible={false}
      editButtonTestId="edit-payment"
      editConfig={{
        onSave: handleSave,
        isPending: updateTransfer.isPending,
        canEdit: can('transfers.edit'),
      }}
      formatOptions={{ locale, currency }}
      trailing={
        transfer.payment_outstanding && can('transfers.request_payment') ? (
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={handleRecordPayment}
            disabled={recordPayment.isPending}
            className="h-auto gap-1.5 px-2 py-1 text-[11px]"
            data-testid="record-payment-received"
          >
            {recordPayment.isPending ? 'Recording…' : 'Mark payment received'}
          </Button>
        ) : undefined
      }
    />
  )
}


// ---------------------------------------------------------------------------
// Champion approval summary - the curated decision card.
//
// Verified ticks the Champion scans before deciding. Each tick is
// computed from data already exposed via existing serializers:
//
//   1. Warranty + property eligible for transfer
//      -> derived from eligibility_snapshot (no BLOCK violations)
//   2. Prior transfers on the property
//      -> list endpoint filtered by host, excluding this transfer
//   3. First contact within the 60-day rule
//      -> days(first_contact_date <-> ownership_change_date) <= 60
//   4. Original owner confirmation of sale
//      -> STUBBED for PR-A; awaits PR-B `original_owner_confirmed` field
//   5. Proof of ownership uploaded
//      -> transfer.proof_of_ownership_verified + proof_of_ownership_file
//      -> OMITTED when this transfer type doesn't require documentation
//
// Layout: flat single-depth rows inside a standard DetailCard, hairline
// rule between rows, semantic icon + colour per tick. The Champion can
// scan top-to-bottom in under five seconds.
// ---------------------------------------------------------------------------

function daysBetweenAbs(a: string | null, b: string | null): number | null {
  if (!a || !b) return null
  const ms = Math.abs(new Date(a).getTime() - new Date(b).getTime())
  return Math.floor(ms / (1000 * 60 * 60 * 24))
}

type TickStatus = 'pass' | 'fail' | 'pending' | 'warn'

function TickRow({
  status,
  label,
  detail,
  isLast,
}: {
  status: TickStatus
  label: string
  detail: string
  isLast?: boolean
}) {
  const config = {
    pass: {
      Icon: Check,
      bgClass: 'bg-[var(--green)]',
      detailClass: 'text-[var(--ink-2)]',
    },
    fail: {
      Icon: X,
      bgClass: 'bg-[var(--status-error)]',
      detailClass: 'text-[var(--status-error)]',
    },
    pending: {
      Icon: Minus,
      bgClass: 'bg-[var(--ink-3)]',
      detailClass: 'text-[var(--ink-3)]',
    },
    warn: {
      Icon: AlertTriangle,
      bgClass: 'bg-[var(--status-warn)]',
      detailClass: 'text-[var(--status-warn)]',
    },
  }[status]

  return (
    <div
      className={`flex items-start gap-4 py-4 ${
        isLast ? '' : 'border-b-[0.5px] border-b-[var(--rule)]'
      }`}
    >
      <div
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-[0.5px] border-transparent text-white ${config.bgClass}`}
      >
        <config.Icon className="h-3 w-3" strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-normal text-[var(--ink)]">{label}</p>
        <p className={`mt-0.5 text-[12px] font-light ${config.detailClass}`}>
          {detail}
        </p>
      </div>
    </div>
  )
}

function ChampionApprovalCard({
  transfer,
  requiresDocumentation,
}: {
  transfer: Transfer
  requiresDocumentation: boolean
}) {
  // Prior-transfer count - list endpoint filtered by host, excluding
  // this transfer. Same query OtherTransfersOnHost uses, deduped by
  // React Query.
  const { data: priorTransfersData } = useTransfers(
    transfer.host_uuid ? { host: transfer.host_uuid } : undefined,
  )
  // Only an approved transfer changed the owner; cancelled or denied ones did not.
  const priorCount = (priorTransfersData?.results ?? []).filter(
    (t) => t.uuid !== transfer.uuid && t.status === 'approved',
  ).length

  // Tick 1 - eligibility (no BLOCK violations on initiation snapshot)
  const blockingViolations = transfer.eligibility_snapshot.filter(
    (v) => v.severity === 'BLOCK',
  )
  const eligibleStatus: TickStatus =
    blockingViolations.length === 0 ? 'pass' : 'fail'
  const eligibleDetail =
    blockingViolations.length === 0
      ? 'Property and registered structures are transferable.'
      : `${blockingViolations.length} blocking rule${
          blockingViolations.length === 1 ? '' : 's'
        } fired at initiation.`

  // Tick 2 - prior transfers
  const priorStatus: TickStatus = priorCount === 0 ? 'pass' : 'warn'
  const priorDetail =
    priorCount === 0
      ? 'No prior transfers recorded on this property.'
      : `${priorCount} prior transfer${
          priorCount === 1 ? '' : 's'
        } on record - review the history below.`

  // Tick 3 - 60-day rule (first_contact within 60 days of closing)
  const daysFromContactToClosing = daysBetweenAbs(
    transfer.first_contact_date,
    transfer.ownership_change_date,
  )
  const within60: TickStatus =
    daysFromContactToClosing === null
      ? 'pending'
      : daysFromContactToClosing <= 60
        ? 'pass'
        : 'warn'
  const within60Detail =
    daysFromContactToClosing === null
      ? 'First-contact or closing date missing.'
      : within60 === 'pass'
        ? `${daysFromContactToClosing} day${
            daysFromContactToClosing === 1 ? '' : 's'
          } between first contact and closing - within the 60-day window.`
        : `${daysFromContactToClosing} days outside the 60-day window. Override reason captured at intake.`

  // Tick 4 - Original owner confirmation (STUBBED until PR-B field lands)
  const originalConfirmedStatus: TickStatus = 'pending'
  const originalConfirmedDetail =
    'Confirmation field ships with PR-B. Captured manually for now.'

  // Tick 5 - New owner proof uploaded + verified. Omitted entirely when
  // this transfer type doesn't require documentation.
  const proofStatus: TickStatus = transfer.proof_of_ownership_verified
    ? 'pass'
    : transfer.proof_of_ownership_file
      ? 'warn'
      : 'fail'
  const proofDetail = transfer.proof_of_ownership_verified
    ? 'Title deed verified by an authorized reviewer.'
    : transfer.proof_of_ownership_file
      ? 'File received, awaiting verification.'
      : 'No proof of ownership uploaded yet.'

  const ticks = [
    { status: eligibleStatus, label: 'Warranty eligible for transfer', detail: eligibleDetail },
    { status: priorStatus, label: 'Prior transfers on this property', detail: priorDetail },
    { status: within60, label: 'First contact within 60-day rule', detail: within60Detail },
    { status: originalConfirmedStatus, label: 'Original owner confirmed sale', detail: originalConfirmedDetail },
    ...(requiresDocumentation
      ? [{ status: proofStatus, label: 'Proof of ownership uploaded', detail: proofDetail }]
      : []),
  ]

  // Headline chip - green when every tick passes, warn when any tick
  // is warn/pending, fail when any tick fails. Drives a single
  // at-a-glance signal the Champion sees before reading the rows.
  const headlineStatus: TickStatus = ticks.some((t) => t.status === 'fail')
    ? 'fail'
    : ticks.some((t) => t.status === 'warn' || t.status === 'pending')
      ? 'warn'
      : 'pass'
  const headlineCopy = {
    pass: { label: 'Ready to approve', cls: 'text-[var(--green)] bg-[var(--green-bg-muted)]' },
    warn: { label: 'Needs your judgement', cls: 'text-[var(--status-warn)] bg-[var(--status-warn-bg)]' },
    fail: { label: 'Action required before approval', cls: 'text-[var(--status-error)] bg-[var(--status-error-bg)]' },
    pending: { label: 'Awaiting verification', cls: 'text-[var(--ink-3)] bg-[var(--bg-subtle)]' },
  }[headlineStatus]

  return (
    <DetailCard
      title="Agent intake summary"
      icon={<ShieldCheck className="h-4 w-4" />}
      collapsible={false}
      trailing={
        <span
          className={`rounded-full px-3 py-1 text-[10px] font-light uppercase tracking-[0.14em] ${headlineCopy.cls}`}
        >
          {headlineCopy.label}
        </span>
      }
    >
      <div className="p-4">
        <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-5">
          {ticks.map((t, i) => (
            <TickRow
              key={t.label}
              status={t.status}
              label={t.label}
              detail={t.detail}
              isLast={i === ticks.length - 1}
            />
          ))}
        </div>

        <p className="mt-4 text-[11px] font-light text-[var(--ink-3)]">
          Each tick is computed from the agent&rsquo;s submitted record and
          the live transfer state. Below: payment, proof of ownership, and
          the agent&rsquo;s notes from the call.
        </p>
      </div>
    </DetailCard>
  )
}

// ---------------------------------------------------------------------------
// Warranty Terms card - the "what am I approving?" moment.
//
// Surfaces the specific policy terms that govern this transfer so the
// Champion doesn't have to cross-reference anything. Every row is
// computed from HoldingWarranty fields already on the wire. Non-
// transferable + fee + window rows carry teaching copy. Graceful:
// hidden if the transfer isn't host-linked OR has no linked warranty.
// ---------------------------------------------------------------------------

function DetailRow({
  label,
  value,
  hint,
  isLast,
}: {
  label: string
  /** String renders with the default dash-fallback; ReactNode renders as-is. */
  value: React.ReactNode
  /** Right-aligned micro-note under the value - use for "why" copy. */
  hint?: string
  isLast?: boolean
}) {
  const rendered =
    typeof value === 'string' || value == null ? value || '-' : value
  return (
    <div
      className={`flex items-start justify-between gap-4 py-2.5 ${
        isLast ? '' : 'border-b-[0.5px] border-b-[var(--rule)]'
      }`}
    >
      <span className="shrink-0 text-[12px] font-light text-[var(--ink-3)]">
        {label}
      </span>
      <div className="flex min-w-0 flex-col items-end gap-1 text-right">
        <span className="truncate text-[13px] font-light text-[var(--ink)]">
          {rendered}
        </span>
        {hint && (
          <span className="text-[11px] font-light text-[var(--ink-3)]">
            {hint}
          </span>
        )}
      </div>
    </div>
  )
}

function formatCoverageLabel(warranty: {
  is_lifetime: boolean
  duration_in_months: number | null
}): string {
  if (warranty.is_lifetime) return 'Lifetime'
  return formatDurationFromMonths(warranty.duration_in_months) || '-'
}

function formatTransferFeeLabel(
  warranty: { transfer_fee?: string | null },
  transfer: Transfer,
): string {
  // Prefer the resolved-fee snapshot persisted on the transfer record
  // (set at initiation). Fall back to the policy-level transfer_fee
  // on the warranty template if the transfer snapshot is blank.
  const amount = transfer.fee_amount ?? warranty.transfer_fee ?? null
  if (!amount || Number(amount) === 0) return 'No fee'
  return `$${Number(amount).toFixed(2)} ${transfer.fee_waived ? '- waived' : ''}`.trim()
}

function WarrantyTermsCard({ transfer }: { transfer: Transfer }) {
  const { formatDate } = useFormatters()
  const params = transfer.host_uuid
    ? { host_uuid: transfer.host_uuid, page_size: '1' }
    : undefined
  const { data: holdingsData, isLoading } = useHoldings(params)

  if (!transfer.host_uuid || isLoading) return null
  const holding = holdingsData?.results?.[0]
  const warranty = holding?.warranties?.[0]
  if (!warranty) return null

  // ``is_transferable`` is per registration: a used allowance makes it false.
  const isTransferable = warranty.is_transferable !== false
  const used = holding?.transfer_count ?? 0
  const max = warranty.max_transfers ?? null
  const limitReached = !isTransferable && max != null && used >= max
  const transferableValue = (
    <span
      className={
        isTransferable
          ? 'text-[var(--ink)]'
          : 'font-normal text-[var(--status-warn)]'
      }
    >
      {isTransferable
        ? '✓ Yes, transferable'
        : limitReached
          ? `✗ Transfer used (${used} of ${max})`
          : '✗ Non-transferable'}
    </span>
  )
  const transferableHint = isTransferable
    ? undefined
    : limitReached
      ? 'Transfer types that do not use the allowance can still be started.'
      : 'Coverage stays with the original owner unless an approver grants an exception.'

  const windowDays = warranty.transfer_window_days ?? null
  const windowValue = windowDays ? `${windowDays} days from closing` : '-'
  const windowHint =
    windowDays === 60
      ? 'OC extends to 100 days internally for goodwill (rare cases: force majeure).'
      : undefined

  const feeLabel = formatTransferFeeLabel(warranty, transfer)
  const feeValue = (
    <span
      className={
        transfer.fee_waived
          ? 'text-[var(--status-warn)]'
          : feeLabel === 'No fee'
            ? 'text-[var(--green)]'
            : 'text-[var(--ink)]'
      }
    >
      {feeLabel}
    </span>
  )
  const feeHint =
    transfer.fee_waived && transfer.fee_waived_reason
      ? transfer.fee_waived_reason
      : undefined

  const documents = warranty.documents ?? []

  return (
    <DetailCard
      title="Warranty terms"
      icon={<ShieldCheck className="h-4 w-4" />}
      collapsible={false}
    >
      <div className="p-4">
        <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-4">
          {warranty.class_name && (
            <DetailRow label="Class" value={warranty.class_name} />
          )}
          <DetailRow label="Type" value={warranty.warranty_type} />
          <DetailRow
            label="Coverage"
            value={formatCoverageLabel(warranty)}
          />
          {warranty.coverage_scope && (
            <DetailRow label="Scope" value={warranty.coverage_scope} />
          )}
          <DetailRow
            label="Active from"
            value={formatDate(warranty.active_from)}
          />
          <DetailRow
            label="Transferable"
            value={transferableValue}
            hint={transferableHint}
          />
          <DetailRow label="Transfer window" value={windowValue} hint={windowHint} />
          <DetailRow
            label="Transfer fee"
            value={feeValue}
            hint={feeHint}
            isLast={documents.length === 0}
          />
        </div>

        {documents.length > 0 && (
          <div className="mt-3">
            <p className="mb-2 text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]">
              Documents
            </p>
            <div className="flex flex-wrap gap-2">
              {documents.map((doc) => (
                <a
                  key={doc.uuid}
                  href={doc.url ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-1 text-[11px] font-light text-[var(--ink-2)] hover:border-[var(--ink-3)] hover:text-[var(--ink)]"
                >
                  <FileText className="h-3 w-3" strokeWidth={1.25} />
                  {doc.label}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Plain-English synthesis of the warranty transfer policy,
            sourced from WarrantyTemplate.terms_synthesis so per-tenant
            copy is seed data, not hardcoded frontend strings. Rendered
            only when the template has a synthesis populated. */}
        {warranty.terms_synthesis && (
          <div className="mt-5 border-t-[0.5px] border-t-[var(--rule)] pt-5">
            <TransferTermsSynthesisSection terms={warranty.terms_synthesis} />
          </div>
        )}
      </div>
    </DetailCard>
  )
}

// ---------------------------------------------------------------------------
// Transfer Terms Synthesis - plain-English restatement of the
// warranty's transfer policy, plus an optional expandable block
// showing the literal policy paragraph. Content is sourced from the
// WarrantyTemplate.terms_synthesis JSONB column via the Holding
// warranty serializer, so tenant-specific copy stays in seed data
// rather than being hardcoded in the frontend.
// ---------------------------------------------------------------------------

function TransferTermsSynthesisSection({
  terms,
}: {
  terms: WarrantyTermsSynthesis
}) {
  const [showFull, setShowFull] = useState(false)
  const [showLiteral, setShowLiteral] = useState(false)
  const allPoints = terms.points ?? []

  // The "At a glance" row doubles as a one-line subtitle under the
  // headline - use it that way and hide it from the expanded list so
  // the content isn't repeated. Matched case-insensitively against the
  // title since seed copy could vary ("At a glance" vs "At-a-glance").
  const atGlance = allPoints.find((p) =>
    /^at[-\s]?a[-\s]?glance$/i.test(p.title.trim()),
  )
  const detailPoints = allPoints.filter((p) => p !== atGlance)

  // Nothing to render if the template was populated with an empty
  // shape - bail out gracefully rather than showing a blank card.
  if (!terms.headline && allPoints.length === 0 && !terms.literal) return null

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <BookOpen
          className="h-3.5 w-3.5 text-[var(--ink-3)]"
          strokeWidth={1.25}
        />
        <p className="text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
          Transfer terms - plain English
        </p>
      </div>
      {terms.headline && (
        <p className="mb-3 font-heading text-[15px] font-light leading-[1.35] text-[var(--ink)]">
          {terms.headline}
        </p>
      )}
      {atGlance && (
        <p className="text-[12px] font-light leading-[1.6] text-[var(--ink-2)]">
          {atGlance.detail}
        </p>
      )}

      {/* Disclosure toggles - compact inline links so the default
          view stays short. "Show full terms" reveals the numbered
          detail list, "Show literal warranty text" reveals the
          literal policy paragraph. Rendered independently so the
          Champion can open either or both. */}
      {(detailPoints.length > 0 || terms.literal) && (
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
          {detailPoints.length > 0 && (
            <Button
              variant="link"
              size="sm"
              onClick={() => setShowFull((v) => !v)}
              className="h-auto gap-1 p-0 text-[11px] font-light uppercase tracking-[0.12em] text-[var(--ink-3)] hover:text-[var(--ink)]"
              data-testid="toggle-full-terms"
            >
              {showFull ? (
                <>
                  <ChevronUp className="h-3 w-3" /> Hide full terms
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3" /> Show full terms
                </>
              )}
            </Button>
          )}
          {terms.literal && (
            <Button
              variant="link"
              size="sm"
              onClick={() => setShowLiteral((v) => !v)}
              className="h-auto gap-1 p-0 text-[11px] font-light uppercase tracking-[0.12em] text-[var(--ink-3)] hover:text-[var(--ink)]"
              data-testid="toggle-literal-terms"
            >
              {showLiteral ? (
                <>
                  <ChevronUp className="h-3 w-3" /> Hide literal warranty text
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3" /> Show literal warranty text
                </>
              )}
            </Button>
          )}
        </div>
      )}

      {showFull && detailPoints.length > 0 && (
        <ol className="mt-4 flex flex-col gap-2.5 animate-in fade-in slide-in-from-top-1 duration-200">
          {detailPoints.map((p, i) => (
            <li
              key={p.title}
              className="flex items-start gap-2.5 border-b-[0.5px] border-b-[var(--rule)] pb-2.5 last:border-b-0 last:pb-0"
            >
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[var(--bg)] text-[9px] font-light text-[var(--ink-3)]">
                {i + 1}
              </span>
              <div className="flex-1">
                <p className="text-[11px] font-normal uppercase tracking-[0.1em] text-[var(--ink-2)]">
                  {p.title}
                </p>
                <p className="mt-0.5 text-[12px] font-light leading-[1.5] text-[var(--ink-2)]">
                  {p.detail}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}

      {showLiteral && terms.literal && (
        <blockquote className="mt-3 rounded-[4px] border-l-[2px] border-l-[var(--rule-strong)] bg-[var(--bg-subtle)] px-4 py-3 text-[12px] font-light leading-[1.6] text-[var(--ink-2)]">
          {terms.literal}
        </blockquote>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Coverage Timeline - visual proration.
//
// Horizontal bar showing warranty coverage from active_from to
// expires_at, with a "now" pin and copy explaining how much is left.
// Graceful: renders a Lifetime pill when is_lifetime=true; hidden when
// active_from is missing. No backend change required.
// ---------------------------------------------------------------------------

function CoverageTimelineCard({ transfer }: { transfer: Transfer }) {
  const { formatDate } = useFormatters()
  const params = transfer.host_uuid
    ? { host_uuid: transfer.host_uuid, page_size: '1' }
    : undefined
  const { data: holdingsData } = useHoldings(params)
  // Freeze the "now" reference at mount so the bar position is stable
  // across re-renders. Called BEFORE any conditional return to satisfy
  // react-hooks/rules-of-hooks.
  const [now] = useState(() => Date.now())

  const warranty = holdingsData?.results?.[0]?.warranties?.[0]
  if (!warranty || !warranty.active_from) return null

  const msPerDay = 1000 * 60 * 60 * 24
  const activeFromMs = new Date(warranty.active_from).getTime()

  // Lifetime warranties - no proration, just surface the fact.
  if (warranty.is_lifetime) {
    return (
      <DetailCard
        title="Coverage"
        icon={<Calendar className="h-4 w-4" />}
        collapsible={false}
      >
        <div className="p-4">
          <div className="flex items-baseline justify-between gap-4">
            <div>
              <p className="text-[13px] font-light text-[var(--ink)]">
                Active from {formatDate(warranty.active_from)}
              </p>
              <p className="mt-1 text-[11px] font-light text-[var(--ink-3)]">
                Lifetime coverage - no proration applies on transfer.
              </p>
            </div>
            <span className="rounded-full bg-[var(--green-bg-muted)] px-3 py-1 text-[10px] font-light uppercase tracking-[0.14em] text-[var(--green)]">
              Lifetime
            </span>
          </div>
        </div>
      </DetailCard>
    )
  }

  // Finite-duration warranties. Prefer expires_at if supplied, else
  // compute from duration_in_months.
  let expiresMs: number | null = null
  if (warranty.expires_at) {
    expiresMs = new Date(warranty.expires_at).getTime()
  } else if (warranty.duration_in_months) {
    const d = new Date(warranty.active_from)
    d.setMonth(d.getMonth() + warranty.duration_in_months)
    expiresMs = d.getTime()
  }
  if (expiresMs === null) return null

  const totalSpan = Math.max(1, expiresMs - activeFromMs)
  const elapsed = Math.max(0, Math.min(totalSpan, now - activeFromMs))
  const percentElapsed = (elapsed / totalSpan) * 100

  const remainingMs = Math.max(0, expiresMs - now)
  const remainingDays = Math.floor(remainingMs / msPerDay)
  const remainingYears = remainingDays / 365
  const remainingLabel =
    remainingDays <= 0
      ? 'Coverage expired'
      : remainingDays < 365
        ? `${remainingDays} day${remainingDays === 1 ? '' : 's'} remaining`
        : `${remainingYears.toFixed(1)} years remaining`

  const endingSoon = remainingDays > 0 && remainingDays < 365

  return (
    <DetailCard
      title="Coverage"
      icon={<Calendar className="h-4 w-4" />}
      collapsible={false}
    >
      <div className="p-4">
        {/* Progress bar with a "now" pin. The fill width is dynamic so we
            use inline style - this is the explicit CLAUDE.md exception
            for "truly dynamic values computed at runtime". */}
        <div className="mb-4">
          <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-[var(--bg-subtle)]">
            <div
              className="absolute left-0 top-0 h-full rounded-full bg-[var(--green)]"
              style={{ width: `${percentElapsed}%` }}
              aria-hidden="true"
            />
          </div>
        </div>

        <div className="flex items-baseline justify-between gap-4 text-[11px] font-light uppercase tracking-[0.1em] text-[var(--ink-3)]">
          <span>From {formatDate(warranty.active_from)}</span>
          <span>
            Expires {warranty.expires_at ? formatDate(warranty.expires_at) : '-'}
          </span>
        </div>

        <div className="mt-4 flex flex-col gap-1 border-t-[0.5px] border-t-[var(--rule)] pt-4">
          <p className="text-[13px] font-light text-[var(--ink)]">
            {remainingLabel}
          </p>
          {endingSoon && (
            <p className="text-[11px] font-light text-[var(--status-warn)]">
              Coverage nearing its end - confirm customer expectations before approval.
            </p>
          )}
          {remainingDays <= 0 && (
            <p className="text-[11px] font-light text-[var(--status-error)]">
              Warranty has expired. Approving this transfer moves expired coverage to the new owner.
            </p>
          )}
        </div>
      </div>
    </DetailCard>
  )
}

// ---------------------------------------------------------------------------
// Champion Review Checklist - manual tick-as-you-go audit memory.
//
// Each item captures an ISO timestamp on tick. State persists per
// transfer UUID in localStorage so the Champion's in-progress review
// survives page reloads - this is her working memory, not the formal
// audit (the Approve/Deny decision is).
// ---------------------------------------------------------------------------

interface ChecklistItem {
  key: string
  label: string
  why: string
}

const CHAMPION_CHECKLIST_ITEMS: ChecklistItem[] = [
  {
    key: 'read_terms',
    label: 'Read the warranty transfer terms above',
    why: 'Confirms the Champion has the one-time-only + 60-day rules fresh in mind.',
  },
  {
    key: 'verified_one_time',
    label: 'Confirmed this is the first transfer on the property',
    why: 'Any further transfers beyond the second owner are not permitted.',
  },
  {
    key: 'verified_window',
    label: 'Verified closing date is within the transfer window',
    why: '60-day rule from the real-estate closing. OC courtesy extends to 100 days.',
  },
  {
    key: 'verified_proof',
    label: 'Proof of ownership looks authentic and matches the buyer',
    why: 'Deed, closing documents, or sales record - name must match the new owner on record.',
  },
  {
    key: 'verified_fee',
    label: 'Transfer fee is correct - or waiver rationale captured',
    why: 'Default up to $100. Waivers require a documented goodwill reason.',
  },
  {
    key: 'reviewed_coverage',
    label: 'Reviewed coverage that carries over to the new owner',
    why: 'Lifetime may convert to fixed-term; labour + TruPro may not transfer.',
  },
  {
    key: 'ready_to_decide',
    label: 'I am ready to make the final decision',
    why: 'The final tick before Approve or Deny.',
  },
]

interface ChecklistState {
  [key: string]: { checkedAt: string } | null
}

function ChampionReviewChecklist({
  transferUuid,
}: {
  transferUuid: string
}) {
  const { locale } = useFormatters()
  const storageKey = `champion-checklist-v1-${transferUuid}`
  const [state, setState] = useState<ChecklistState>({})
  const [loaded, setLoaded] = useState(false)

  // Rehydrate from localStorage on mount. We read inside an effect so
  // the render stays pure (SSR-safe shape too, in case this ever moves
  // to RSC). Missing / corrupted entries fall back to an empty map.
  useEffect(() => {
    if (typeof window === 'undefined') {
      setLoaded(true)
      return
    }
    try {
      const raw = window.localStorage.getItem(storageKey)
      if (raw) {
        const parsed = JSON.parse(raw)
        if (parsed && typeof parsed === 'object') {
          setState(parsed as ChecklistState)
        }
      }
    } catch {
      // Ignore parse / quota errors - the checklist is a best-effort
      // memory aid, not critical state.
    } finally {
      setLoaded(true)
    }
  }, [storageKey])

  // Persist every change. Guarded by the `loaded` flag so the first
  // render doesn't overwrite localStorage with the empty initial
  // state before rehydration completes.
  useEffect(() => {
    if (!loaded || typeof window === 'undefined') return
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(state))
    } catch {
      // Storage might be full or disabled - silently tolerate.
    }
  }, [state, storageKey, loaded])

  const toggle = (key: string) => {
    setState((prev) => {
      const next = { ...prev }
      if (prev[key]) {
        // Untick: clear the entry.
        next[key] = null
      } else {
        next[key] = { checkedAt: new Date().toISOString() }
      }
      return next
    })
  }

  const resetAll = () => {
    setState({})
  }

  const completed = CHAMPION_CHECKLIST_ITEMS.filter((i) => state[i.key]).length
  const total = CHAMPION_CHECKLIST_ITEMS.length
  const allDone = completed === total

  return (
    <DetailCard
      title="Your review checklist"
      icon={<ClipboardCheck className="h-4 w-4" />}
      collapsible={false}
      trailing={
        <span
          className={`rounded-full px-3 py-1 text-[10px] font-light uppercase tracking-[0.14em] ${
            allDone
              ? 'bg-[var(--green-bg-muted)] text-[var(--green)]'
              : 'bg-[var(--bg-subtle)] text-[var(--ink-3)]'
          }`}
          data-testid="checklist-progress"
        >
          {completed} of {total}
        </span>
      }
    >
      <div className="p-4">
        <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-5">
          {CHAMPION_CHECKLIST_ITEMS.map((item, i) => {
            const entry = state[item.key]
            const isLast = i === CHAMPION_CHECKLIST_ITEMS.length - 1
            return (
              <label
                key={item.key}
                className={`flex cursor-pointer items-start gap-3 py-3 ${
                  isLast ? '' : 'border-b-[0.5px] border-b-[var(--rule)]'
                }`}
              >
                <Checkbox
                  checked={!!entry}
                  onCheckedChange={() => toggle(item.key)}
                  className="mt-0.5"
                  data-testid={`checklist-${item.key}`}
                />
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-[13px] ${
                      entry
                        ? 'font-light text-[var(--ink-2)]'
                        : 'font-normal text-[var(--ink)]'
                    }`}
                  >
                    {item.label}
                  </p>
                  <p className="mt-0.5 text-[11px] font-light text-[var(--ink-3)]">
                    {item.why}
                  </p>
                  {entry && (
                    <p className="mt-1 text-[10px] font-light uppercase tracking-[0.12em] text-[var(--green)]">
                      Verified {formatChecklistTime(entry.checkedAt, locale)}
                    </p>
                  )}
                </div>
              </label>
            )
          })}
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <p className="text-[11px] font-light text-[var(--ink-3)]">
            Your personal review memory - saved in this browser. The
            official audit is the Approve or Deny decision.
          </p>
          {completed > 0 && (
            <Button
              variant="link"
              size="sm"
              onClick={resetAll}
              className="h-auto shrink-0 gap-1 p-0 text-[11px] font-light uppercase tracking-[0.12em] text-[var(--ink-3)] hover:text-[var(--ink)]"
              data-testid="checklist-reset"
            >
              <RotateCcw className="h-3 w-3" /> Reset
            </Button>
          )}
        </div>
      </div>
    </DetailCard>
  )
}

function formatChecklistTime(iso: string, locale: string): string {
  try {
    const d = new Date(iso)
    const today = new Date()
    const sameDay = d.toDateString() === today.toDateString()
    if (sameDay) {
      return `at ${d.toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: '2-digit',
      })}`
    }
    return `on ${d.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
    })} at ${d.toLocaleTimeString(locale, {
      hour: 'numeric',
      minute: '2-digit',
    })}`
  } catch {
    return iso
  }
}

// ---------------------------------------------------------------------------
// Eligibility snapshot panel - surfaces what rules fired at initiation.
// ---------------------------------------------------------------------------

function EligibilitySnapshotPanel({
  snapshot,
}: {
  snapshot: EligibilityViolation[]
}) {
  if (!snapshot || snapshot.length === 0) return null

  return (
    <DetailCard
      title="Eligibility at Initiation"
      icon={<AlertTriangle className="h-4 w-4" />}
      collapsible={false}
    >
      <div className="p-4">
        <ul className="flex flex-col gap-2">
          {snapshot.map((v) => (
            <li
              key={v.rule_uuid}
              className="flex items-start gap-2 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-3"
            >
              <SeverityPill severity={v.severity} />
              <div>
                <p className="text-[13px] text-[var(--ink)]">{v.name}</p>
                <p className="mt-0.5 text-[12px] text-[var(--ink-2)]">{v.message}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </DetailCard>
  )
}

function TransferDetailSkeleton() {
  const pulse = 'animate-pulse rounded-[4px] bg-[var(--rule)]'
  return (
    <div aria-label="Loading transfer" aria-busy="true">
      {/* Header */}
      <div className={`${pulse} mb-2 h-3 w-20`} />
      <div className={`${pulse} mb-6 h-9 w-64`} />
      {/* Signal strip */}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4"
          >
            <div className={`${pulse} mb-2 h-2.5 w-16`} />
            <div className={`${pulse} h-4 w-24`} />
          </div>
        ))}
      </div>
      {/* Two-col main */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5"
            >
              <div className={`${pulse} mb-3 h-2.5 w-20`} />
              <div className={`${pulse} mb-2 h-4 w-40`} />
              <div className={`${pulse} h-3 w-56`} />
            </div>
          ))}
        </div>
        <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5">
          <div className={`${pulse} mb-3 h-2.5 w-28`} />
          {[0, 1, 2].map((i) => (
            <div key={i} className={`${pulse} mb-2 h-10 w-full`} />
          ))}
        </div>
      </div>
    </div>
  )
}


// ---------------------------------------------------------------------------
// Decisions & Audit - override_reason + approval/denial reasons
// ---------------------------------------------------------------------------

function ReviewedReason({
  label,
  reason,
  reviewedByEmail,
  reviewedAt,
}: {
  label: string
  reason: string
  reviewedByEmail?: string | null
  reviewedAt?: string | null
}) {
  const { formatDateTime } = useFormatters()
  return (
    <div>
      <p className="mb-1 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
        {label}
        {reviewedByEmail ? (
          <span className="ml-2 normal-case tracking-normal text-[10px] text-[var(--ink-3)]">
            by {reviewedByEmail}
          </span>
        ) : null}
        {reviewedAt ? (
          <span className="ml-2 normal-case tracking-normal text-[10px] text-[var(--ink-3)]">
            on {formatDateTime(reviewedAt)}
          </span>
        ) : null}
      </p>
      <p className="text-[13px] text-[var(--ink-2)]">{reason}</p>
    </div>
  )
}

function DecisionsAuditCard({ transfer }: { transfer: Transfer }) {
  const overrideReason = transfer.override_reason?.trim() ?? ''
  const approvalReason = transfer.approval_reason
  const denialReason = transfer.denial_reason
  const hasContent =
    overrideReason.length > 0 || !!approvalReason || !!denialReason
  if (!hasContent) return null

  return (
    <DetailCard
      title="Decisions & Audit"
      icon={<ShieldAlert className="h-4 w-4" />}
      collapsible={false}
    >
      <div className="flex flex-col gap-3 p-4">
        {overrideReason.length > 0 && (
          <div>
            <p className="mb-1 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Override reason
              <span className="ml-2 normal-case tracking-normal text-[10px] text-[var(--ink-3)]">
                captured at initiation
              </span>
            </p>
            <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-[var(--ink-2)]">
              {overrideReason}
            </p>
          </div>
        )}
        {approvalReason && (
          <ReviewedReason
            label="Approval reason"
            reason={approvalReason.label}
            reviewedByEmail={transfer.reviewed_by_email}
            reviewedAt={transfer.reviewed_at}
          />
        )}
        {denialReason && (
          <ReviewedReason
            label="Denial reason"
            reason={denialReason.label}
            reviewedByEmail={transfer.reviewed_by_email}
            reviewedAt={transfer.reviewed_at}
          />
        )}
      </div>
    </DetailCard>
  )
}

// ---------------------------------------------------------------------------
// Linked registrations - source <-> created holding back-references
// ---------------------------------------------------------------------------

const LINKED_REASON_LABEL: Record<string, string> = {
  not_active: 'Not active',
  not_transferable: 'Not transferable',
  coverage_expired: 'Coverage expired',
  transfer_window_passed: 'Window passed',
  prior_transfer_limit: 'Transfer limit reached',
}

type LinkedRow = NonNullable<Transfer['transfer_registrations']>[number]

// LinkedRegistrationsCard - outcome-grouped, dense single-line rows.
//
// The Champion's primary attention is on the rows being included in
// the transfer. Ineligible rows are noise during the decision: they
// were auto-voided at intake and the Champion only needs them when
// asking "why was this excluded?". So the card splits by outcome,
// renders the included set inline as dense single-line rows, and
// collapses the ineligible set behind a single summary line that
// expands on demand.
function LinkedRegistrationsCard({ transfer }: { transfer: Transfer }) {
  const rows = transfer.transfer_registrations ?? []
  if (rows.length === 0) return null

  const approved = transfer.status === 'approved'
  const included = rows.filter((r) => r.is_eligible)
  // Approved rows were updated in place; denied or cancelled ones never moved.
  const outcomeLabel = approved
    ? 'Updated in place'
    : ['denied', 'cancelled'].includes(transfer.status)
      ? 'Not transferred'
      : 'Pending approval'
  const ineligible = rows.filter((r) => !r.is_eligible)
  const ineligibleVerb = approved ? 'voided' : 'not transferred'

  return (
    <DetailCard
      title="Linked Registrations"
      icon={<FileText className="h-4 w-4" />}
      collapsible={false}
      trailing={
        <p className="text-[11px] font-light text-[var(--ink-3)]">
          {included.length} included
          {ineligible.length > 0
            ? ` · ${ineligible.length} ${ineligibleVerb}`
            : ''}
        </p>
      }
    >
      <div className="p-4">
        {included.length > 0 && (
          <IncludedRegistrationsSection
            rows={included}
            outcomeLabel={outcomeLabel}
            showHeader={ineligible.length > 0}
          />
        )}

        {ineligible.length > 0 && (
          <IneligibleRegistrationsSection
            rows={ineligible}
            approved={approved}
            hasIncluded={included.length > 0}
          />
        )}
      </div>
    </DetailCard>
  )
}

// Claims filed on the transfer's registrations, so a decision is made knowing
// what is open on them. Open claims are listed first.
// ---------------------------------------------------------------------------
// Property records - deed owner and last recorded sale, for reference
// ---------------------------------------------------------------------------

type PropertyRecords = NonNullable<Transfer['property_records']>

function PropertyRecordsCard({ records }: { records: PropertyRecords }) {
  const { t } = useTranslation()
  const { formatDate } = useFormatters()
  const state = records.status === 'found' ? records.outcome || 'found' : records.status
  const badges: Record<string, ['success' | 'warning' | 'neutral', string, string]> = {
    match: [
      'success',
      t('transfers.propertyRecords.match', 'Matches new owner'),
      t('transfers.propertyRecords.matchNote', 'The deed names the new owner.'),
    ],
    not_recorded: [
      'neutral',
      t('transfers.propertyRecords.notRecorded', 'Sale not recorded yet'),
      t(
        'transfers.propertyRecords.notRecordedNote',
        'The deed still names the previous owner. A sale can take weeks to be recorded.',
      ),
    ],
    mismatch: [
      'warning',
      t('transfers.propertyRecords.mismatch', 'Ownership Mismatch'),
      t(
        'transfers.propertyRecords.mismatchNote',
        'The deed names someone else, or a later sale. You can still go ahead; add a note on what you checked.',
      ),
    ],
    found: [
      'neutral',
      t('transfers.propertyRecords.found', 'Record found'),
      t('transfers.propertyRecords.foundNote', 'Compare the deed with the transfer details.'),
    ],
    not_found: [
      'neutral',
      t('transfers.propertyRecords.notFound', 'Not Found'),
      t(
        'transfers.propertyRecords.notFoundNote',
        'No property records were found for this address. The transfer goes ahead as normal.',
      ),
    ],
    failed: [
      'warning',
      t('transfers.propertyRecords.failed', 'Lookup failed'),
      t(
        'transfers.propertyRecords.failedNote',
        'The property records lookup did not complete. The transfer goes ahead as normal.',
      ),
    ],
    pending: [
      'neutral',
      t('transfers.propertyRecords.pending', 'Checking'),
      t('transfers.propertyRecords.pendingNote', 'Looking up the property records.'),
    ],
    unsupported: [
      'neutral',
      t('transfers.propertyRecords.unsupported', 'US only'),
      t(
        'transfers.propertyRecords.unsupportedNote',
        'Property records are only available for US properties.',
      ),
    ],
  }
  const [tone, label, note] = badges[state] ?? badges.found
  const saleNotes: Record<string, string> = {
    matches: t('transfers.propertyRecords.saleMatches', 'Matches the closing date'),
    earlier: t('transfers.propertyRecords.saleEarlier', 'Before the closing date'),
    later: t('transfers.propertyRecords.saleLater', 'After the closing date'),
  }
  const saleDate = saleNotes[records.sale_date_match ?? '']
  const row = (code: string, name: string, value: string): CardFieldItem => ({
    field: defineField({ code_name: code, display_name: name, field_type: 'TEXT' }),
    value,
    readOnly: true,
  })
  const items: CardFieldItem[] = []
  if (records.status === 'found') {
    items.push(
      row(
        'deed_owner',
        t('transfers.propertyRecords.deedOwner', 'Deed owner'),
        records.deed_owner_full_name || '-',
      ),
      row(
        'deed_sale_date',
        t('transfers.propertyRecords.lastSale', 'Last recorded sale'),
        records.deed_sale_date
          ? [formatDate(records.deed_sale_date), saleDate].filter(Boolean).join(' · ')
          : '-',
      ),
    )
  }
  if (records.checked_at) {
    items.push(
      row(
        'checked_at',
        t('transfers.propertyRecords.checked', 'Checked'),
        formatDate(records.checked_at),
      ),
    )
  }
  items.push(row('note', t('transfers.propertyRecords.meaning', 'What it means'), note))
  return (
    <EditableDetailCard
      title={t('transfers.propertyRecords.title', 'Property records')}
      icon={<Landmark className="h-4 w-4" />}
      testId="property-records-card"
      trailing={<SignalBadge tone={tone}>{label}</SignalBadge>}
      items={items}
    />
  )
}

function LinkedClaimsCard({ transfer }: { transfer: Transfer }) {
  const { t } = useTranslation()
  const claims = transfer.linked_claims ?? []
  const open = claims.filter((c) => c.is_open).length
  return (
    <DetailCard
      title="Linked Claims"
      icon={<FileText className="h-4 w-4" />}
      collapsible={false}
      trailing={
        <p className="text-[11px] font-light text-[var(--ink-3)]">{open} open</p>
      }
    >
      <div className="p-4">
        {claims.length === 0 ? (
          <p className="text-[12px] text-[var(--ink-3)]">
            {t(
              'transfers.detail.noClaims',
              'No {{claimNounPluralLower}} on these {{holdingNounPluralLower}}.',
            )}
          </p>
        ) : (
          <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
            {claims.map((claim, i) => (
              <div
                key={claim.uuid}
                className={`flex items-center justify-between gap-3 px-4 py-2.5 ${
                  i === claims.length - 1 ? '' : 'border-b-[0.5px] border-b-[var(--rule)]'
                }`}
                data-testid="linked-claim-row"
              >
                <a
                  href={`/claims/${claim.uuid}`}
                  className="font-mono text-[12px] font-medium text-[var(--ink)] underline decoration-[var(--ink-3)] underline-offset-[3px] hover:decoration-[var(--ink)]"
                >
                  {claim.claim_number || formatClaimId(claim.uuid)}
                </a>
                <span
                  className={`text-[12px] ${
                    claim.is_open ? 'text-[var(--ink)]' : 'font-light text-[var(--ink-3)]'
                  }`}
                >
                  {claim.status_label || '-'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </DetailCard>
  )
}

function IncludedRegistrationsSection({
  rows,
  outcomeLabel,
  showHeader,
}: {
  rows: LinkedRow[]
  outcomeLabel: string
  // Hide the section header when there's no ineligible counterpart -
  // the card-level title already establishes context, so a second
  // "Included for transfer (N)" label would just be visual chrome.
  showHeader: boolean
}) {
  return (
    <div>
      {showHeader && (
        <p className="mb-2 text-[10px] font-normal uppercase tracking-[0.12em] text-[var(--ink-3)]">
          Included for transfer ({rows.length})
        </p>
      )}
      <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
        {rows.map((row, i) => (
          <IncludedRegistrationRow
            key={row.uuid}
            row={row}
            outcomeLabel={outcomeLabel}
            isLast={i === rows.length - 1}
          />
        ))}
      </div>
    </div>
  )
}

function IncludedRegistrationRow({
  row,
  outcomeLabel,
  isLast,
}: {
  row: LinkedRow
  outcomeLabel: string
  isLast: boolean
}) {
  const destination = row.created_holding_uuid ? (
    <a
      href={`/registrations/${row.created_holding_uuid}`}
      title="Open new registration"
      className="font-mono text-[12px] font-medium text-[var(--ink)] underline decoration-[var(--ink-3)] underline-offset-[3px] hover:decoration-[var(--ink)]"
    >
      {formatHoldingId(row.created_holding_uuid)}
    </a>
  ) : (
    <span className="text-[12px] font-light text-[var(--ink-3)]">
      {outcomeLabel}
    </span>
  )

  return (
    <div
      className={`flex items-center gap-3 px-4 py-2.5 ${
        isLast ? '' : 'border-b-[0.5px] border-b-[var(--rule)]'
      }`}
      data-testid="linked-included-row"
    >
      <a
        href={`/registrations/${row.holding_uuid}`}
        title="Open source registration"
        className="font-mono text-[12px] font-medium text-[var(--ink)] underline decoration-[var(--ink-3)] underline-offset-[3px] hover:decoration-[var(--ink)]"
      >
        {formatHoldingId(row.holding_uuid)}
      </a>
      <ArrowRight
        className="h-3 w-3 shrink-0 text-[var(--ink-3)]"
        strokeWidth={1.25}
        aria-hidden="true"
      />
      {destination}
    </div>
  )
}

function IneligibleRegistrationsSection({
  rows,
  approved,
  hasIncluded,
}: {
  rows: LinkedRow[]
  approved: boolean
  hasIncluded: boolean
}) {
  // Default expanded when there's nothing in the Included list -
  // the collapsed summary would otherwise be the only thing on the
  // card, and forcing a click to see the only content is hostile UX.
  const [expanded, setExpanded] = useState(!hasIncluded)

  // If every ineligible row shares the same void_reason_code, the
  // group-level summary can state it once and per-row reasons are
  // suppressed (they would just repeat the summary). If the codes
  // are mixed, the summary degrades to "multiple reasons" and each
  // expanded row carries its own reason inline so the Champion can
  // still scan the breakdown.
  const codes = new Set(
    rows.map((r) => r.void_reason_code).filter(Boolean) as string[],
  )
  const sharedCode = codes.size === 1 ? Array.from(codes)[0] : null
  const sharedReasonLabel = sharedCode
    ? LINKED_REASON_LABEL[sharedCode] ?? 'Ineligible'
    : null
  // Prefer the human-readable detail copy when every row carries the
  // same one - it usually reads better than the short label
  // ("Registration status is Cancelled" vs "Not active").
  const sharedDetail =
    sharedCode &&
    rows.every((r) => r.void_reason_detail === rows[0].void_reason_detail)
      ? rows[0].void_reason_detail
      : null

  const verb = approved ? 'voided' : 'not transferred'
  const summarySuffix =
    sharedDetail || sharedReasonLabel || 'multiple reasons'
  const summary = `${rows.length} registration${
    rows.length === 1 ? '' : 's'
  } ${verb} - ${summarySuffix}`

  return (
    <div className={hasIncluded ? 'mt-4' : ''}>
      <Button
        type="button"
        variant="ghost"
        onClick={() => setExpanded((v) => !v)}
        className="flex h-auto w-full items-center justify-start gap-2 rounded-[4px] px-2 py-1.5 text-left text-[12px] font-light text-[var(--ink-2)] hover:bg-[var(--bg-subtle)] hover:text-[var(--ink)]"
        data-testid="ineligible-toggle"
        aria-expanded={expanded}
      >
        <ChevronRight
          className={`h-3 w-3 shrink-0 text-[var(--ink-3)] transition-transform duration-150 ${
            expanded ? 'rotate-90' : ''
          }`}
          strokeWidth={1.5}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1 truncate">{summary}</span>
        <span className="shrink-0 text-[10px] font-light uppercase tracking-[0.12em] text-[var(--ink-3)]">
          {expanded ? 'Hide' : 'Show details'}
        </span>
      </Button>

      {expanded && (
        <div
          className="mt-2 flex flex-wrap gap-1.5 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-2.5 animate-in fade-in slide-in-from-top-1 duration-150"
          data-testid="ineligible-list"
        >
          {rows.map((row) => (
            <IneligibleRegistrationPill
              key={row.uuid}
              row={row}
              showReason={!sharedReasonLabel}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function IneligibleRegistrationPill({
  row,
  showReason,
}: {
  row: LinkedRow
  showReason: boolean
}) {
  const reasonLabel = row.void_reason_code
    ? LINKED_REASON_LABEL[row.void_reason_code] ?? 'Ineligible'
    : 'Ineligible'

  return (
    <a
      href={`/registrations/${row.holding_uuid}`}
      title={
        row.void_reason_detail
          ? `${reasonLabel} - ${row.void_reason_detail}`
          : reasonLabel
      }
      className="inline-flex items-center gap-1.5 rounded-full border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-2.5 py-1 font-mono text-[11px] text-[var(--ink-2)] transition-colors hover:border-[var(--ink)] hover:bg-[var(--bg-subtle)] hover:text-[var(--ink)]"
    >
      <span>{formatHoldingId(row.holding_uuid)}</span>
      {showReason && (
        <span className="font-sans text-[10px] font-light text-[var(--ink-3)]">
          {' · '}
          {reasonLabel}
        </span>
      )}
    </a>
  )
}

// ---------------------------------------------------------------------------
// Compliance history - other transfers on the same host
// ---------------------------------------------------------------------------

function OtherTransfersOnHost({
  hostUuid,
  excludeUuid,
}: {
  hostUuid: string
  excludeUuid: string
}) {
  const navigate = useNavigate()
  const { formatDate } = useFormatters()
  const { data, isLoading } = useTransfers({ host: hostUuid })
  const list = (data?.results ?? []).filter((t) => t.uuid !== excludeUuid)

  if (isLoading || list.length === 0) return null

  return (
    <DetailCard
      title="Other transfers on this property"
      icon={<FileText className="h-4 w-4" />}
      collapsible={false}
    >
      <div className="p-4">
        <ul className="flex flex-col gap-1.5">
          {list.map((t) => (
            <li key={t.uuid}>
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate(`/transfers/${t.uuid}`)}
                className="flex h-auto w-full items-center justify-between gap-3 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-3 text-left transition-colors hover:border-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
              >
                <div className="min-w-0">
                  <p className="truncate text-[13px] text-[var(--ink)]">
                    {t.new_owner_first_name} {t.new_owner_last_name}
                  </p>
                  <p className="text-[11px] text-[var(--ink-3)]">
                    {t.transfer_type?.label ?? '-'} {'· '}
                    {t.ownership_change_date
                      ? formatDate(t.ownership_change_date)
                      : 'no date'}
                  </p>
                </div>
                <StatusBadge status={t.status_label ?? t.status} />
              </Button>
            </li>
          ))}
        </ul>
      </div>
    </DetailCard>
  )
}

function ChannelMetadataCard({
  metadata,
}: {
  metadata: Record<string, unknown>
}) {
  const entries = Object.entries(metadata ?? {}).filter(
    ([, v]) => v !== null && v !== undefined && v !== '',
  )
  if (entries.length === 0) return null
  return (
    <DetailCard
      title="Channel Metadata"
      icon={<Info className="h-4 w-4" />}
      collapsible={false}
    >
      <div className="p-4">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[12px]">
          {entries.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-[var(--ink-3)]">{k.replace(/_/g, ' ')}</dt>
              <dd className="font-mono text-[var(--ink-2)]">{String(v)}</dd>
            </div>
          ))}
        </dl>
      </div>
    </DetailCard>
  )
}

// ---------------------------------------------------------------------------
// Activity - the record's workflow-engine history, rendered with the SAME
// shared components the claims record uses: a DetailCard wrapping the shared
// WorkflowTimelineEntry, fed by the generic useWorkflowHistory(instanceUuid).
// No bespoke timeline - transfers and claims render identically.
// ---------------------------------------------------------------------------

function ActivityCard({ history }: { history: ActionLogEntry[] }) {
  const { locale } = useFormatters()
  // §1/§16: clicking a timeline entry opens the shared ActionEntryDialog, which
  // shows the full detail of the event - including a real old -> new field diff
  // for "Details edited" - instead of the row being inert. Same dialog the
  // claims record uses.
  const [activeEntry, setActiveEntry] = useState<ActionLogEntry | null>(null)
  return (
    <DetailCard
      title="Activity"
      icon={<Clock className="h-3.5 w-3.5" />}
      collapsible={false}
    >
      {history.length > 0 ? (
        <div className="max-h-[420px] overflow-y-auto px-4 py-4">
          <div className="pl-5">
            {history.map((entry, index) => (
              <WorkflowTimelineEntry
                key={entry.uuid}
                entry={entry}
                showConnector={index < history.length - 1}
                onClick={() => setActiveEntry(entry)}
              />
            ))}
          </div>
        </div>
      ) : (
        <p className="px-4 py-3 text-[13px] text-[var(--ink-3)]">No activity yet</p>
      )}
      <ActionEntryDialog
        entry={activeEntry}
        open={activeEntry !== null}
        onOpenChange={(open) => {
          if (!open) setActiveEntry(null)
        }}
        roleLabels={SENDER_ROLE_LABELS}
        formatTimestamp={(iso) => {
          const d = new Date(iso)
          if (isNaN(d.getTime())) return iso
          return new Intl.DateTimeFormat(locale, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }).format(d)
        }}
      />
    </DetailCard>
  )
}

// ---------------------------------------------------------------------------
// Request Documents capture dialog - captures the message that appears
// in the email, an optional Cc list, and whether to set up an
// automatic reminder. Wired to BOTH the review-panel "Request
// Documents" action and the Proof card's "Resend email" button, since
// the backend now requires a message on this endpoint.
// ---------------------------------------------------------------------------

function RequestDocumentsDialog({
  open,
  onOpenChange,
  transfer,
  mainContactEmail,
  mainContactName,
  pending,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  transfer: Transfer
  mainContactEmail: string
  mainContactName: string
  pending: boolean
  onConfirm: (result: {
    message: string
    cc_emails: string[]
    setup_reminder: boolean
    auto_cancel: boolean
    requested_document_keys: string[]
    recipient_email: string
  }) => void
}) {
  const { t } = useTranslation()
  // §10b: the transfer's document checklist (tenant-configurable), so the
  // reviewer can pick what to request - like a claim's "request additional
  // documentation". Fetched only while the dialog is open.
  const { data: checklist } = useTransferDocumentChecklist(transfer.uuid, open)
  const [message, setMessage] = useState('')
  const [messageError, setMessageError] = useState(false)
  const [showCc, setShowCc] = useState(false)
  const [ccInput, setCcInput] = useState('')
  const [setupReminder, setSetupReminder] = useState(false)
  const [autoCancel, setAutoCancel] = useState(false)
  const [recipient, setRecipient] = useState('')
  const [recipientError, setRecipientError] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())

  const greetingName =
    recipient && recipient === mainContactEmail && mainContactName
      ? mainContactName.split(' ')[0]
      : transfer.new_owner_first_name || 'there'

  // Re-seed from the transfer's current values every time the dialog
  // opens (it stays mounted between opens, so a plain useState
  // initializer would only apply once).
  useEffect(() => {
    if (!open) return
    const prefillCc = (transfer.document_request_cc ?? []).join(', ')
    setMessage(transfer.document_request_message ?? '')
    setMessageError(false)
    setRecipientError(false)
    setCcInput(prefillCc)
    setShowCc(prefillCc.length > 0)
    // WPS-277: a new request starts from the account's reminder settings.
    setSetupReminder(
      !!transfer.auto_reminder_enabled || !!transfer.request_defaults?.setup_reminder,
    )
    setAutoCancel(!!transfer.request_defaults?.auto_cancel)
    // §10b/§3: "email link to" defaults to the main contact, then the new owner.
    setRecipient(mainContactEmail || transfer.new_owner_email || '')
  }, [
    open,
    transfer.document_request_message,
    transfer.document_request_cc,
    transfer.auto_reminder_enabled,
    transfer.request_defaults?.setup_reminder,
    transfer.request_defaults?.auto_cancel,
    transfer.new_owner_email,
    mainContactEmail,
  ])

  // Pre-tick every checklist item once the list loads for this open.
  useEffect(() => {
    if (!open || !checklist) return
    setSelectedKeys(new Set(checklist.map((c) => c.key)))
  }, [open, checklist])

  const toggleKey = (key: string) =>
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const selectedItems = (checklist ?? []).filter((c) => selectedKeys.has(c.key))

  const handleConfirm = () => {
    const trimmedMessage = message.trim()
    const trimmedRecipient = recipient.trim()
    let invalid = false
    if (!trimmedMessage) {
      setMessageError(true)
      invalid = true
    }
    if (!trimmedRecipient) {
      setRecipientError(true)
      invalid = true
    }
    if (invalid) return
    const cc_emails = ccInput
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean)
    onConfirm({
      message: trimmedMessage,
      cc_emails,
      setup_reminder: setupReminder,
      auto_cancel: setupReminder && autoCancel,
      requested_document_keys: [...selectedKeys],
      recipient_email: trimmedRecipient,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request documents</DialogTitle>
          <DialogDescription>
            Choose what to request and who to send it to. Your message appears in
            the body of the email previewed below.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div>
            <Label className="clara-section-label mb-1.5 block">
              Email link to{' '}
              <span className="text-[var(--status-error)]">*</span>
            </Label>
            <Input
              type="email"
              value={recipient}
              onChange={(e) => {
                setRecipient(e.target.value)
                if (recipientError && e.target.value.trim()) setRecipientError(false)
              }}
              placeholder="email@example.com"
              className={
                recipientError
                  ? 'border-[var(--status-error)] ring-1 ring-[var(--status-error)]'
                  : ''
              }
              data-testid="request-docs-recipient"
            />
            {recipientError ? (
              <p className="mt-1 text-[11px] text-[var(--status-error)]">
                A recipient is required.
              </p>
            ) : (
              <p className="mt-1 text-[11px] text-[var(--ink-3)]">
                Defaults to the main contact.
              </p>
            )}
          </div>

          <div>
            <Label className="clara-section-label mb-1.5 block">What to request</Label>
            <div className="flex flex-col gap-2">
              {(checklist ?? []).length === 0 ? (
                <p className="text-[12px] text-[var(--ink-3)]">
                  Proof of ownership and any supporting documents.
                </p>
              ) : (
                (checklist ?? []).map((item) => (
                  <label
                    key={item.key}
                    className="flex cursor-pointer items-start gap-2.5"
                  >
                    <Checkbox
                      checked={selectedKeys.has(item.key)}
                      onCheckedChange={() => toggleKey(item.key)}
                      className="mt-0.5"
                      data-testid={`request-docs-item-${item.key}`}
                    />
                    <span className="text-[13px] font-light text-[var(--ink-2)]">
                      {item.label}
                      {item.required ? (
                        <span className="text-[var(--ink-3)]"> (required)</span>
                      ) : null}
                      {item.description ? (
                        <span className="block text-[11px] text-[var(--ink-3)]">
                          {item.description}
                        </span>
                      ) : null}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div>
            <Label className="clara-section-label mb-1.5 block">
              Note to the customer{' '}
              <span className="text-[var(--status-error)]">*</span>
            </Label>
            <Textarea
              value={message}
              onChange={(e) => {
                setMessage(e.target.value)
                if (messageError && e.target.value.trim()) setMessageError(false)
              }}
              rows={4}
              placeholder="Explain what's needed and by when."
              className={`resize-y text-[13px] leading-relaxed ${
                messageError
                  ? 'border-[var(--status-error)] ring-1 ring-[var(--status-error)]'
                  : ''
              }`}
              data-testid="request-docs-message"
            />
            {messageError && (
              <p
                className="mt-1 text-[11px] text-[var(--status-error)]"
                data-testid="request-docs-message-error"
              >
                A message is required.
              </p>
            )}
          </div>

          <div>
            {showCc ? (
              <div>
                <Label className="clara-section-label mb-1.5 block">Cc</Label>
                <Input
                  value={ccInput}
                  onChange={(e) => setCcInput(e.target.value)}
                  placeholder="email@example.com, another@example.com"
                  data-testid="request-docs-cc"
                />
              </div>
            ) : (
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => setShowCc(true)}
                className="h-auto gap-1 p-0 text-[11px] font-light uppercase tracking-[0.12em] text-[var(--ink-3)] hover:text-[var(--ink)]"
                data-testid="request-docs-add-cc"
              >
                + Add Cc
              </Button>
            )}
          </div>

          <label className="flex cursor-pointer items-start gap-2.5">
            <Checkbox
              checked={setupReminder}
              onCheckedChange={(v) => setSetupReminder(v === true)}
              className="mt-0.5"
              data-testid="request-docs-auto-reminder"
            />
            <span className="text-[13px] font-light text-[var(--ink-2)]">
              Set up automatic reminder
            </span>
          </label>
          {setupReminder && (
            <label className="-mt-2 ml-6 flex cursor-pointer items-start gap-2.5">
              <Checkbox
                checked={autoCancel}
                onCheckedChange={(v) => setAutoCancel(v === true)}
                className="mt-0.5"
                data-testid="request-docs-auto-cancel"
              />
              <span className="text-[13px] font-light text-[var(--ink-2)]">
                {t('transfers.requestDocs.cancelIfNothing', 'Cancel the transfer if nothing arrives')}
                <span className="block text-[11px] text-[var(--ink-3)]">
                  {t(
                    'transfers.requestDocs.cancelIfNothingHint',
                    'After the last reminder. A cancelled transfer does not count toward the transfer limit.',
                  )}
                </span>
              </span>
            </label>
          )}

          {/* §10 (comment 10797): show the FULL documents-request email the
              recipient receives - the reviewer's message inline, then the
              standard checklist - so this request is not a bare message box that
              looks nothing like what ships. */}
          <div>
            <Label className="clara-section-label mb-1.5 block">Preview</Label>
            <div
              className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-4"
              data-testid="request-docs-preview"
            >
              <p className="mb-2 text-[14px] font-semibold text-[var(--ink)]">
                Upload your transfer documents
              </p>
              <p className="mb-2 text-[13px] leading-relaxed text-[var(--ink-2)]">
                Hi {greetingName},
              </p>
              <p className="mb-2 text-[13px] leading-relaxed text-[var(--ink-2)]">
                To complete the warranty transfer, we need a few documents from
                you.
              </p>
              {message.trim() !== '' && (
                <p className="mb-2 whitespace-pre-line text-[13px] leading-relaxed text-[var(--ink)]">
                  {message}
                </p>
              )}
              <p className="mb-1 text-[13px] font-medium text-[var(--ink)]">
                What to send
              </p>
              <ul className="mb-3 list-disc pl-5 text-[13px] leading-relaxed text-[var(--ink-2)]">
                {selectedItems.length === 0 ? (
                  <li>Proof of Ownership and any supporting documents.</li>
                ) : (
                  selectedItems.map((item) => (
                    <li key={item.key}>
                      {item.label}
                      {item.required ? ' (required)' : ''}
                      {item.description ? ` - ${item.description}` : ''}
                    </li>
                  ))
                )}
              </ul>
              <span className="inline-block rounded-[6px] bg-[var(--ink)] px-3 py-1.5 text-[12px] font-medium text-[var(--bg)]">
                Upload your documents
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={pending}
            data-testid="request-docs-confirm"
          >
            {pending ? 'Sending…' : 'Send request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Request Payment capture dialog - optional recipient override.
// ---------------------------------------------------------------------------

function RequestPaymentDialog({
  open,
  onOpenChange,
  transfer,
  mainContactEmail,
  pending,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  transfer: Transfer
  mainContactEmail: string
  pending: boolean
  onConfirm: (result: { recipient_email?: string; message?: string }) => void
}) {
  const [recipientEmail, setRecipientEmail] = useState('')
  const [message, setMessage] = useState('')
  const { formatCurrency } = useFormatters()

  // §11 (comment 10797): default the payment-request recipient to the transfer's
  // main contact (the payer), falling back to an explicit override already on
  // the record, then the new owner. The agent can still edit it. Seed the value
  // ONLY on the closed -> open transition (tracked via a ref) so a background
  // query refetch that updates these props while the dialog is open never
  // silently overwrites the agent's manual edit.
  const prevPaymentOpen = useRef(false)
  useEffect(() => {
    if (open && !prevPaymentOpen.current) {
      setRecipientEmail(
        transfer.payment_request_email ||
          mainContactEmail ||
          transfer.new_owner_email ||
          '',
      )
      setMessage('')
    }
    prevPaymentOpen.current = open
  }, [open, transfer.payment_request_email, transfer.new_owner_email, mainContactEmail])

  const isCheck = transfer.payment_method === 'check'
  const feeLabel =
    transfer.fee_amount != null
      ? formatCurrency(Number(transfer.fee_amount))
      : 'the transfer fee'

  const handleConfirm = () => {
    onConfirm({
      recipient_email: recipientEmail.trim() || undefined,
      message: message.trim() || undefined,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request payment</DialogTitle>
          <DialogDescription>
            Sends payment instructions to the main contact. Add an optional note
            and review the preview below before sending.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div>
            <Label className="clara-section-label mb-1.5 block">
              Send payment request to
            </Label>
            <Input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder={
                mainContactEmail ||
                transfer.new_owner_email ||
                'main-contact@example.com'
              }
              data-testid="request-payment-email"
            />
          </div>

          <div>
            <Label className="clara-section-label mb-1.5 block">
              Add a note (optional)
            </Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Supplemental instructions - appears in the email body."
              className="resize-y text-[13px] leading-relaxed"
              data-testid="request-payment-message"
            />
          </div>

          {/* §11: preview of the payment-request email (check vs online) - with
              the note - so the agent sees what is sent before sending. */}
          <div>
            <Label className="clara-section-label mb-1.5 block">Preview</Label>
            <div
              className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-4"
              data-testid="request-payment-preview"
            >
              <p className="mb-2 text-[14px] font-semibold text-[var(--ink)]">
                Complete your transfer payment
              </p>
              <p className="mb-2 text-[13px] leading-relaxed text-[var(--ink-2)]">
                To finalise the warranty transfer, a payment of{' '}
                <strong>{feeLabel}</strong> is required.
              </p>
              {message.trim() !== '' && (
                <p className="mb-2 whitespace-pre-line text-[13px] leading-relaxed text-[var(--ink)]">
                  {message}
                </p>
              )}
              {isCheck ? (
                <>
                  <p className="mb-1 text-[13px] font-medium text-[var(--ink)]">
                    Pay by check
                  </p>
                  <p className="text-[13px] leading-relaxed text-[var(--ink-2)]">
                    Mail a check for {feeLabel}. Full remittance details (payee
                    and mailing address) are included in the email.
                  </p>
                </>
              ) : (
                <>
                  <p className="mb-1 text-[13px] font-medium text-[var(--ink)]">
                    Pay online
                  </p>
                  <p className="text-[13px] leading-relaxed text-[var(--ink-2)]">
                    Secure electronic payment instructions are included in the
                    email.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={pending}
            data-testid="request-payment-confirm"
          >
            {pending ? 'Sending…' : 'Send request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SeverityPill({ severity }: { severity: EligibilityViolation['severity'] }) {
  const classes =
    severity === 'BLOCK'
      ? 'bg-[var(--status-error-bg)] text-[var(--status-error)]'
      : severity === 'WARN'
        ? 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]'
        : 'bg-[var(--bg-subtle)] text-[var(--ink-3)]'
  const Icon = severity === 'INFO' ? Info : AlertTriangle
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.06em] ${classes}`}
    >
      <Icon className="h-3 w-3" />
      {severity}
    </span>
  )
}
